/* eslint-disable arrow-body-style */

// 公共方法
import React from 'react';
import { history } from 'umi';
import moment from 'moment';
import { v4 as uuid } from 'uuid';
import { cloneDeep } from 'lodash-es';
// @ts-ignore
import html2pdf from 'html2pdf.js';
import { message, notification, FormItemProps } from 'tea-component';
import { reportOperationLog } from '@gp/services/global';
import {
  captureRemoveClassName,
  captureExpendClassName,
} from '@intelligence/utils/captureUtils';
import { BoolFlag } from '@gp/enums/common';
import { ALL_REPORT_OPERATION_LOG } from '@gp/constants/operationLogParams';
import {
  getReverseObj,
  SLASH_G_POP,
  IMAGE_SUFFIXES,
  COS_PREVIEWABLE_FILE_SUFFIXES,
  PREVIEWABLE_FILE_SUFFIXES,
  DOWNLOADABLE_FILE_SUFFIXES,
  MENU_ID_MAP,
  CONSTANT_TEXTS,
  OHERSKEY,
  VIDEO_SUFFIXES,
  COS_PREVIEWABLE_FILE_SUFFIXES_ADD_VIDEO,
} from '@gp/constants/const';
import { GROUP_KEY } from '@gp/constants/select';

import type { MomentInput } from 'moment';
import type { AxiosRequestConfig } from 'axios';
import type { FieldRenderProps } from 'react-final-form-hooks/dist';
import type { SortBy } from 'tea-component/lib/table/addons';
import type { INullableMoment } from '@home/types/common';
import type { ISortOrder } from '@intelligence/pages/GenreProfile/typings';
import type {
  IMockIdable,
  IRecord,
  IMaterialVO,
  ISelectFilter,
} from '@gp/types/global';
import type { IRiskVO } from '@gp/types/projectUpdate';
import type { IFile } from '@gp/components/FileList/types';

// 后端没有开发环境，本地调试使用，转测置为空
export const TEMP_TOKEN = '';

// message 1.内容 2.存在时长 3.是否需要关闭按钮 4.提示类型 success error warning
interface messageTipProps {
  content?: React.ReactNode;
  duration?: number;
  isClose?: boolean;
  type?: string;
  style?: { [keyName: string]: any; };
}

export const ERROR_TIP_STYLE = {
  backgroundColor: '#fcecec',
  border: '1px solid #f6b5b5',
  height: 40,
  color: '#B43537',
};

// 空函数，用于作为Function类型变量的默认值
export const noop = () => {};

export const messagetip: (options: messageTipProps) => void = ({
  content,
  duration = 3000,
  isClose,
  type,
}) => {
  const messageClose = message.warning({
    content: (
      <div style={{ color: 'red' }}>
        {content}
        {isClose && (
          <span
            className="close"
            onClick={() => {
              messageClose.hide();
            }}
          ></span>
        )}
      </div>
    ),
    duration,
    className: type,
    style: ERROR_TIP_STYLE,
  });
};

export const messagetipSuccess: (options: messageTipProps) => void = ({
  content,
  duration = 5000,
  isClose = true,
  type,
}) => {
  const messageClose = message.warning({
    content: (
      <div style={{ color: 'red' }}>
        {content}
        {isClose && (
          <span
            className="close"
            onClick={() => {
              messageClose.hide();
            }}
          ></span>
        )}
      </div>
    ),
    duration,
    className: type,
    style: ERROR_TIP_STYLE,
  });
};

export const ERROR_MESSAGE = {
  400: '发出的请求有错误，服务器没有进行新建或修改数据的操作。',
  401: '用户没有权限（令牌、用户名、密码错误）。',
  403: '用户得到授权，但是访问是被禁止的。',
  404: '发出的请求针对的是不存在的记录，服务器没有进行操作。',
  406: '请求的格式不可得。',
  410: '请求的资源被永久删除，且不会再得到的。',
  422: '当创建一个对象时，发生一个验证错误。',
  500: '服务器发生错误，请检查服务器。',
  502: '网关错误。',
  503: '服务不可用，服务器暂时过载或维护。',
  504: '网关超时。',
  default: '未知错误',
};

export const ERROR_CODE_MESSAGE: Record<number, string> = {
  999: '系统错误',
};

export const NETWORK_ERROR_MSG =
  'Network error, please check the network and try again later';

export const INSTANCE_CONFIG: AxiosRequestConfig = {
  baseURL: '/',
  method: 'post',
  withCredentials: true,
  timeout: 50000,
  maxContentLength: 5 * 1000 * 1000,
  headers: {
    'Content-Type': 'application/json',
    'X-Requested-With': 'XMLHttpRequest',
  },
  validateStatus(_status) {
    return true;
  },
  // options: {},
};

export const isNullOrUndefined = (data: any) =>
  data === undefined || data === null;

export const isEmpty = (data: any) => {
  return (
    isNullOrUndefined(data) ||
    (typeof data === 'string' && data.trim().length === 0) ||
    (typeof data === 'number' && isNaN(data))
  );
};

// 判定是否为非数组 或者 空数组
export const isEmptyList = (v?: any): boolean => !Array.isArray(v) || !v.length;

export const toOptions = (
  list?: string[] | null,
  formatter?: (payload?: string) => string,
): IGPSelectOptionWithGroup[] => {
  if (!Array.isArray(list) || !list.length) return [];
  return list.map((item) => {
    const textTmp = formatter?.(item) ?? item;
    return { groupKey: GROUP_KEY, value: item, text: textTmp, label: textTmp };
  });
};

export function toStdOptions<T = IRecord<any>>(
  list?: T[] | null,
  keys?: { text: keyof T; value: keyof T; },
): IGPSelectOptionWithGroup[] {
  if (!Array.isArray(list) || !list.length) return [];

  const {
    text: textKey = 'name' as keyof T,
    value: valueKey = 'id' as keyof T,
  } = keys || {};

  return list.map((item) => {
    const text = (item?.[textKey] as string) || '';
    const value = (item?.[valueKey] as string) || '';
    return { text, value };
  });
}

export const mappingToOptions = (
  names?: string[],
  mappings?: IRecord<string> | null,
): IGPSelectOptionWithGroup[] => {
  if (!Array.isArray(names) || !names.length) return [];
  if (!mappings) return [];
  const entries = Object.entries(mappings);
  return names.map((name) => {
    const entry = entries.find((entry) => entry[1] === name);
    const value = entry?.[0] || '';
    return { groupKey: GROUP_KEY, value, text: name || '', label: name || '' };
  });
};

/**
 * 通过url入参对象，返回页面需要的 edition_unified_id id_type entity_type
 * @param data url路径带参的对象
 * @returns
 */
export function getUrlData(data: {
  entity_type: string;
  id_type?: string;
  unified_edition_id: string;
  mobile_edition_id?: string;
}) {
  let edition_unified_id;
  let id_type;
  let entity_type;
  if (data.entity_type === 'mobile') {
    if (
      data.id_type === 'unified_id' ||
      data.unified_edition_id === data.mobile_edition_id
    ) {
      edition_unified_id = data.unified_edition_id;
      id_type = 'unified_id';
    } else {
      edition_unified_id = data.mobile_edition_id || data.unified_edition_id;
      id_type = 'edition_id';
    }
    entity_type = 'mobile';
  } else {
    entity_type = data.entity_type;
    edition_unified_id = data.unified_edition_id;
    id_type = 'edition_id';
  }
  return {
    edition_unified_id,
    id_type,
    entity_type,
  };
}

// 获取全局的参数对象
export function handleGlobalParams() {
  const sessionGameInfo = JSON.parse(
    localStorage.getItem('OpinionGameInfo') || '{}',
  );
  const info = {
    edition_unified_id: '',
    id_type: '',
    entity_type: '',
  };
  if (JSON.stringify(sessionGameInfo) == '{}') {
  } else {
    const GameInfoData = getUrlData(sessionGameInfo);
    info.edition_unified_id = GameInfoData.edition_unified_id;
    info.id_type = GameInfoData.id_type;
    info.entity_type = GameInfoData.entity_type;
  }
  return info;
}

/**
 * sentiment 转换 规则 1=> 4 5 ，2=> 3 , 3=> 2,1
 * 原因：筛选器回传的数组是[1,2,3] 而api接口需要的参数是[5,4,3,2,1]
 * @param arr
 * @returns
 */
export function transSentiment(arr: number[]) {
  const transArr: number[] = [];
  if (arr) {
    arr.map((v) => {
      if (v === 1) {
        transArr.push(4, 5);
      }
      if (v === 2) {
        transArr.push(3);
      }
      if (v === 3) {
        transArr.push(1, 2);
      }
    });
  }
  return transArr;
}

// 获取全局的用户user_id
export function getSessionGlobalUserId() {
  const sessionUserInfo = localStorage.getItem('userID') || '95';
  return sessionUserInfo;
}

const getUnitV = (
  value: number,
  currentUnit = '',
  nextUnit = '',
  decimal = 2,
) => {
  const _preRes = value.toFixed ? value.toFixed(decimal) : value;
  if (parseInt(String(_preRes))?.toString()?.length > 3 && nextUnit) {
    return '1.'.padEnd(2 + decimal, '0') + nextUnit;
  }
  return _preRes + currentUnit;
};

/**
 * 单位处理
 * @param value
 * @param intNoDot:整数是否去掉小数点
 * @param decimal 保留多少位小数
 * @returns
 */
export function getShowCount(
  value: number,
  intNoDot: Boolean = false,
  decimal = 2,
) {
  if (isNaN(Number(value))) {
    return value;
  }
  let res = null;
  if (value >= 1000000000) {
    const sen = value / 1000000000;
    res = getUnitV(sen, 'B', '', decimal);
  } else if (value >= 1000000) {
    const sen = value / 1000000;
    res = getUnitV(sen, 'M', 'B', decimal);
  } else if (value >= 1000) {
    const sen = value / 1000;
    res = getUnitV(sen, 'K', 'M', decimal);
  } else {
    res = getUnitV(value, '', 'M', decimal);
  }
  // 整数去掉小数点
  if (intNoDot && typeof res === 'string') {
    const lastChar = res?.slice(-1) || '';
    if ('BMK'.includes(lastChar)) {
      const num = res?.slice(0, -1);
      if (parseInt(num) === parseFloat(num)) res = parseInt(num) + lastChar;
    } else {
      if (parseInt(res) === parseFloat(res)) res = parseInt(res);
    }
  }

  return res !== null ? res : value;
}

// 数组去重
export function deleteArray(arr1: any[], arr2: any[]) {
  let a;
  let b;
  for (let i = arr1?.length - 1;i >= 0;i--) {
    a = arr1[i];
    for (let j = arr2?.length - 1;j >= 0;j--) {
      b = arr2[j];
      if (a == b) {
        arr1.splice(i, 1);
        arr2.splice(j, 1);
        break;
      }
    }
  }
  return arr1 || [];
}

// 数组合并
export function MergeArray(arr1: any[], arr2: any[]): any {
  const _arr = [];
  for (let i = 0;i < arr1?.length;i++) {
    _arr.push(arr1[i]);
  }
  for (let i = 0;i < arr2?.length;i++) {
    let flag = true;
    for (let j = 0;j < arr1?.length;j++) {
      if (arr2[i] == arr1[j]) {
        flag = false;
        break;
      }
    }
    if (flag) {
      _arr.push(arr2[i]);
    }
  }
  return _arr;
}

// 数组判断包含
export function isContainArr(parent: any[], child: any[]) {
  return child.every((item) => {
    return parent.some((v) => {
      return item == v;
    });
  });
}

// 返回数组1中存在数组2中元素的集合
export function isExistArr(arr1: any[], arr2: any[]) {
  let a;
  let b;
  const c: any = [];
  for (let i = arr1?.length - 1;i >= 0;i--) {
    a = arr1[i];
    for (let j = arr2?.length - 1;j >= 0;j--) {
      b = arr2[j];
      if (a == b) {
        c.push(a);
        break;
      }
    }
  }
  return c;
}

/**
 * 使用match方法实现模糊查询
 * @param  {Array}  list     进行查询的数组
 * @param  {String} keyWord  查询的关键词
 * @return {Array}           查询的结果
 */
export function fuzzyQuery(list: string[], keyWord: string) {
  const arr: any = [];
  for (let i = 0;i < list.length;i++) {
    if (list[i].match(keyWord) != null) {
      arr.push(list[i]);
    }
  }
  return arr;
}

/**
 * 前端分页处理
 */
export function pagingOption(
  pageIndex: number,
  pageSize: number,
  dataList: never[],
) {
  return new Promise((resolve) => {
    setTimeout(
      () =>
        resolve({
          records: getTableData(pageIndex, pageSize, dataList),
        }),
      2000,
    );
  });
}

/**
 * @name  getTableData
 * @desc  纯JS前端分页方法
 * @param  {Number} page 当前页码，默认1
 * @param  {Number} pageSize 每页最多显示条数，默认10
 * @param  {Array} totalData 总的数据集，默认为空数组
 * @return {Object} {
    data, //当前页展示数据，数组
    page, //当前页码
    pageSize, //每页最多显示条数
    length, //总的数据条数
  }
**/
const getTableData = (page: number, pageSize: number, totalData: never[]) => {
  const { length } = totalData;
  const tableData = {
    data: [],
    page,
    pageSize,
    length,
  };
  if (pageSize >= length) {
    // pageSize大于等于总数据长度，说明只有1页数据或没有数据
    tableData.data = totalData;
    tableData.page = 1; // 直接取第一页
  } else {
    // pageSize小于总数据长度，数据多余1页
    const num = pageSize * (page - 1); // 计算当前页（不含）之前的所有数据总条数
    if (num < length) {
      // 如果当前页之前所有数据总条数小于（不能等于）总的数据集长度，则说明当前页码没有超出最大页码
      const startIndex = num; // 当前页第一条数据在总数据集中的索引
      const endIndex = num + pageSize - 1; // 当前页最后一条数据索引
      tableData.data = totalData.filter(
        (_, index) => index >= startIndex && index <= endIndex,
      ); // 当前页数据条数小于每页最大条数时，也按最大条数范围筛取数据
    } else {
      // 当前页码超出最大页码，则计算实际最后一页的page，自动返回最后一页数据
      const size = Math.floor(length / pageSize); // 取商
      const rest = length % pageSize; // 取余数
      if (rest > 0) {
        // 余数大于0，说明实际最后一页数据不足pageSize，应该取size+1为最后一条的页码
        tableData.page = size + 1; // 当前页码重置，取size+1
        tableData.data = totalData.filter(
          (_, index) => index >= pageSize * size && index <= length,
        );
      } else if (rest === 0) {
        // 余数等于0，最后一页数据条数正好是pageSize
        tableData.page = size; // 当前页码重置，取size
        tableData.data = totalData.filter(
          (_, index) => index >= pageSize * (size - 1) && index <= length,
        );
      } // 注：余数不可能小于0
    }
  }
  return tableData;
};

/**
 * 标记指定关键字高亮（以关键字的特殊字符分隔后的数组单个值为单位，不区分大小写）
 * @param {string} text 需要处理的原字符串
 * @param {string} key  需要标记的字符串关键字
 */
// export function keyRender(text, key) {
//   if (key && text && typeof text === 'string' && typeof key === 'string') {
//     // const keyArr = key.split(specialCharater).filter((k) => k)
//     const newText = text.replace(new RegExp(key, 'ig'), (str) => `<Fragment style="background: #CCE3FF;  padding: 2px 5px;   line-height: 14px;">${str}</Fragment>`)
//     return <span dangerouslySetInnerHTML={{ __html: newText }} />
//   } else {
//     return text
//   }
// }

/**
 * 设置游戏参数
 * @param end_time 时间
 * @param time_type 时间类型
 * @returns
 */
type TimeType = 'hourly' | 'daily' | 'weekly' | 'monthly';
export function changeEndTime(end_time: MomentInput, time_type: TimeType) {
  if (time_type == 'hourly') {
    return moment(end_time).format('YYYY-MM-DD HH:mm:ss');
  }
  if (time_type == 'daily') {
    return moment(end_time)
      .add(1, 'd')
      .subtract(1, 's')
      .format('YYYY-MM-DD HH:mm:ss');
  }
  if (time_type == 'weekly') {
    return moment(end_time)
      .add(1, 'd')
      .subtract(1, 's')
      .format('YYYY-MM-DD HH:mm:ss');
  }
  if (time_type == 'monthly') {
    return moment(end_time)
      .add(1, 'M')
      .subtract(1, 's')
      .format('YYYY-MM-DD HH:mm:ss');
  }
}

// 计算日期差
/** *
 * ps 只选择一天或者一个星期或者一个月时使用
 * @param dateArry 日期数组
 * @param timeType 当前日期类型
 * @returns granularity
 */
export function dateDiff(
  dateArry: [MomentInput, MomentInput],
  timeType: TimeType,
) {
  const start_date = moment(dateArry[0]).min(0).seconds(0);
  const end_date = moment(changeEndTime(dateArry[1], timeType));
  const dateDiffs = end_date.diff(start_date, 'day');
  const ledateDiffs = end_date.diff(start_date, 'hour');
  let granularity = 'hourly';
  if (timeType === 'daily' || timeType === 'hourly') {
    granularity = ledateDiffs <= 24 ? 'hourly' : timeType;
  } else if (timeType === 'weekly') {
    granularity = dateDiffs <= 6 ? 'daily' : timeType;
  } else {
    granularity = dateDiffs <= 30 ? 'weekly' : timeType;
  }
  return granularity;
}

// 计算日期差
/** *
 * ps 只选择一天或者一个星期或者一个月时使用
 * @param dateArry 日期数组
 * @param timeType 当前日期类型
 * @returns granularity
 */
export function dateDiff_copy(
  start_time: MomentInput,
  end_time: MomentInput,
  timeType: TimeType,
) {
  const start_date = moment(start_time).min(0).seconds(0);
  const end_date = moment(end_time);
  const dateDiffs = end_date.diff(start_date, 'day');
  const ledateDiffs = end_date.diff(start_date, 'hour');
  let granularity = false;
  if (timeType === 'hourly') {
    return granularity;
  }
  if (timeType === 'daily') {
    granularity = ledateDiffs <= 24;
  } else if (timeType === 'weekly') {
    granularity = dateDiffs <= 6;
  } else {
    granularity = dateDiffs <= 30;
  }
  return granularity;
}

/**
 * 获取无数据返回语句
 * @param index 状态 1：没加载出来/请求失败/超时 2：无数据(主要是table和echarts数据)
 * @returns
 */
export function getNoResultTip(index: number) {
  switch (index) {
    case 1:
      return 'Loading failed, please refresh the page.';
    case 2:
      return 'No data under current filter selections.';
    case 666:
      return '';
    default:
      return 'Sorry, no data yet.';
  }
}

// channel一分三
export function getSubChannelValue(list: string[]) {
  const arr: any = { data1: [], data2: [], data3: [] };
  for (let i = 0;i < list.length;i++) {
    const channel = list[i].substring(0, list[i].indexOf('@'));
    if (channel === 'social') {
      arr.data1.push(
        list[i].substring(list[i].indexOf('@') + 1, list[i].length),
      );
    }
    if (channel === 'comments') {
      arr.data2.push(
        list[i].substring(list[i].indexOf('@') + 1, list[i].length),
      );
    }
    if (channel === 'news') {
      arr.data3.push(
        list[i].substring(list[i].indexOf('@') + 1, list[i].length),
      );
    }
  }
  return arr;
}

/**
 * 筛选器数据格式化处理
 * @param data 筛选器数据
 * @param gameInfo 游戏info
 * @returns api请求所需入参
 */

export function getFilterdata(
  data: Record<string, any>,
  gameInfo: Record<string, any>,
) {
  if (
    data.channel_all !== true &&
    JSON.stringify(data.channel_all) !== JSON.stringify([1, 1, 1])
  ) {
    data.channel_all = false;
  }

  let reviewsRating = [];
  let monitorScource = [];
  let platform = [];
  if (gameInfo.entity_type === 'mobile') {
    reviewsRating = data.reviews_rating_all ? [] : data.reviews_rating;
  }
  if (gameInfo.entity_type === 'pc') {
    reviewsRating = data.reviews_rating_pc_all ? [] : data.reviews_rating_pc;
    monitorScource = data.monitor_scource_all ? [] : data.monitor_scource;
  }
  if (gameInfo.entity_type === 'console') {
    reviewsRating = data.reviews_rating_pc_all ? [] : data.reviews_rating_pc;
    monitorScource = data.monitor_scource_all ? [] : data.monitor_scource;
    platform = data.platform_all ? [] : data.platform;
  }

  const keywordData: Array<string> = replaceKeywords(data.keyword);
  // const keyworkKey = ['Common', 'Customized', 'Buzzword']
  // for (let item of data.keyword || []) {
  //   for (let key of keyworkKey) {
  //     if (item.includes(key)) {
  //       keywordData.push(item.replace(`${key}-`, ''))
  //       break
  //     }
  //   }
  // }
  return {
    channel_social: data.channel_all
      ? ['9999']
      : getSubChannelValue(data.channel).data1,
    channel_comments: data.channel_all
      ? ['9999']
      : getSubChannelValue(data.channel).data2,
    channel_news: data.channel_all
      ? ['9999']
      : getSubChannelValue(data.channel).data3,
    language: data.language_all ? [] : data.language,
    sentiment: data.sentiment_all ? [] : transSentiment(data.sentiment),
    social_media_by_keyword: data.social_media_by_keyword,
    social_media_by_account: data.social_media_by_account,
    purpose: data.purpose_all ? [] : data.purpose,
    is_valid: data.is_valid_all ? [] : data.is_valid,
    keyword: data.keyword_all ? [] : keywordData,
    region: data.region[0] === 'GLOBAL' ? [] : data.region,
    region_type: data.region_type,
    topic: data.topic_all ? [] : data.topic,
    start_time: moment(data.currentTime[0])
      .second(0)
      .minute(0)
      .format('YYYY-MM-DD HH:mm:ss'),
    end_time: changeEndTime(data.currentTime[1], data.dateType),
    // date_type: dateDiff(data.currentTime,data.dateType),
    date_type: data.dateType,
    version: data.version_all ? [] : data.version,
    reviews_rating: reviewsRating,
    monitor_scource: monitorScource,
    platform,
    version_time: data.version_time,
  };
}

/**
 * 对个别channel进行一个翻译
 * @param key 需要转译的channel字段
 * @returns 转译后的
 */
export function transChannel(key: string) {
  const channel: Record<string, string> = {
    youtube_keyword: 'Youtube',
    google_keyword: 'Google News',
  };
  if (channel[key]) {
    return channel[key];
  }
  return key;
}

/**
 * 去除keyword组件中key
 * @param list
 * @returns
 */
export function replaceKeywords(list: any): Array<string> {
  const keywordData: Array<string> = [];
  const keyworkKey = ['Common', 'Customized', 'Buzzword'];
  for (const item of list || []) {
    let flag = true;
    for (const key of keyworkKey) {
      if (item.includes(key)) {
        keywordData.push(item.replace(`${key}-`, ''));
        flag = false;
        break;
      }
    }
    if (flag) {
      keywordData.push(item);
    }
  }
  return keywordData;
}

export const k: number = Math.pow(10, 3); // 1000
export const m: number = Math.pow(10, 6); // 1百万
export const b: number = Math.pow(10, 9); // 10亿

/**
 * 去除keyword组件中key
 * @param target 传进来的目标值
 * @returns object 返回initUnit目标值所在单位内区间的单位  和initData返回目标值所在单位内区间的 四舍五入后的值
 */
// 获取单位 被单位整除单位后的值 和对应的单位
export const getUnitData = (target: number): object => {
  // 处理柱状图的单位转换问题
  let initUnit = ''; // 单位
  let initData = 0; // 在当前单位返回内值(被除单位后的)
  if (m > target && target >= k) {
    initUnit = 'K';
    initData = target / k;
  } else if (b > target && target >= m) {
    initUnit = 'M';
    initData = target / m;
  } else if (target >= b) {
    initUnit = 'B';
    initData = target / b;
  } else if (k > target) {
    initUnit = '';
    initData = target;
  }
  return { initUnit, initData: initData.toFixed(2) }; // 四舍五入
};

/**
 * 去除keyword组件中key（处理柱状图的单位转换问题）
 * @param target 传进来的目标值
 * @param currentUnit 传进来的值 当前单位
 * @param currentUnit 传进来的目标值 要转化成的目标单位
 * @returns initData  传进来的目标值转化成目标单位后的总数
 */
export const getDataByUnit = (
  target: number,
  currentUnit: string,
  targetUnit: string,
): number => {
  let initData = 0;
  if (currentUnit === 'B' && targetUnit === 'M') {
    // 十亿转化为百万
    initData = target * k;
  } else if (currentUnit === 'B' && targetUnit === 'K') {
    // 亿转化为K
    initData = target * m;
  } else if (currentUnit === 'B' && targetUnit === '') {
    // 十转化为少于1000的
    initData = target * b;
  } else if (currentUnit === 'M' && targetUnit === 'K') {
    // 百万转化为K
    initData = target * k;
  } else if (currentUnit === 'M' && targetUnit === '') {
    // 百万转化为少于1000的
    initData = target * m;
  }
  return initData;
};

/**
 * 判断是否是有效数字
 * @param val 传进来的值
 * @returns 判断结果
 */
export const isValidNumber = (val: any): boolean => {
  if (isNaN(Number(val))) return false;
  return !isNaN(parseFloat(val)) && isFinite(Number(val));
};

/**
 * 判断是否为正数
 * @param val 传进来的值
 * @returns 判断结果
 */
export const isPositiveNum = (val: string) => {
  if (isNaN(Number(val))) return false;
  // 判断是否是正数
  const reg = /^\d+(?=\.{0,1}\d+$|$)/;
  return reg.test(val);
};

/**
 * 获取>=0数据转换（B/M/K单位）的结果
 * @param v 数据
 * @param decimal 保留多为位小数
 * @param isEndZero 是否保留小数点后面的0
 * @returns 结果
 */
const getFormatNum = (
  v: string | number,
  decimal = 2,
  isEndZero = true,
): string => {
  if (!v && String(v) !== '0') return '-'; // 除0外的无效值
  const num = Number(v);
  let res =
    isPositiveNum(String(num)) || num === 0
      ? `${getShowCount(num, false, decimal)}`
      : '-';
  const noEndZeroVal = parseFloat(res);
  if (!isNaN(noEndZeroVal) && !isEndZero) {
    const endStr = res?.slice(-1);
    res = noEndZeroVal + (['B', 'M', 'K']?.includes(endStr) ? endStr : '');
  }
  return res;
};

// 支持正数、0、负数
export const getFormatNumPlus = (
  v: string | number,
  decimal = 2, // 保留多少位小数
  isEndZero = true, // 是否保留小数点后面的0
) => {
  const isValid = isValidNumber(v);
  if (!isValid) return '-';
  let valueTemp: any = Number(v);
  let isNegativeNum = false;
  isNegativeNum = valueTemp < 0;
  valueTemp = getFormatNum(Math.abs(valueTemp), decimal, isEndZero);
  return isNegativeNum ? `-${valueTemp}` : valueTemp;
};

// 加美元$符展示
export const getFormatNumPlusOfUSD = (
  v: string | number | undefined,
  decimal = 2, // 保留多少位小数
  isEndZero = true, // 是否保留小数点后面的0
) => {
  if (!v) return '';
  const tempRes = getFormatNumPlus(v, decimal, isEndZero);

  return !tempRes || tempRes == '-' ? tempRes : `$${tempRes}`;
};

// 有效数值加千分位展示，支持负数，会把末尾无效的0去掉
export const getNumberOfDotUnit = (v: any): string => {
  const isValid = isValidNumber(v);
  if (!isValid) return v ? v : '-';
  const valueTemp: any = Number(v);
  let isNegativeNum = false;
  isNegativeNum = valueTemp < 0;
  const absValues = String(Math.abs(valueTemp))?.split('.');
  const dotL = absValues?.[0] || '';
  const dotR = absValues?.[1] || '';
  let dotValueL = '';
  const dotLStrArr = dotL?.split('')?.reverse();
  const len = dotL?.length;
  // 加千分位逗号
  if (len) {
    for (let i = 0, l = len;i < l;i++) {
      const t = dotLStrArr?.[i];
      const f = (i + 1) % 3 === 0 && i < len - 1;
      dotValueL += t + (f ? ',' : '');
    }
    if (dotValueL) {
      dotValueL = dotValueL?.split('')?.reverse()?.join('');
    }
  }
  let resultV = dotValueL;
  if (dotR) {
    resultV += `.${dotR}`;
  }
  return isNegativeNum ? `-${resultV}` : resultV;
};

/**
 * 任意数取整（四舍五入）
 * @param v 任意数
 * @returns 整数（四舍五入）
 */
export const getRoundIntegerNum = (v: any, placeholder = '-', dot = true) => {
  const isValid = isValidNumber(v);
  if (!isValid) return placeholder || '-';
  const num = Number(v);
  return dot ? getNumberOfDotUnit(Math.round(num)) : Math.round(num);
};

export const getRoundIntegerNumEmpty = (
  ...args: Parameters<typeof getRoundIntegerNum>
) => {
  const result = getRoundIntegerNum(...args);
  return getPureEmptyValue(result);
};

/**
 * 获取>=0数据转换（%单位）的结果
 * @param v 数据
 * @param decimal 保留多少位小数
 * @param isEndZero 是否保留小数点后面的0
 * @returns 结果
 */
const getFormatPercent = (
  v: string | number,
  decimal = 0, // 保留整数
  isEndZero = true,
): string => {
  if (!v && String(v) !== '0') return '-'; // 除0外的无效值
  const num = Number(v);
  let res = '-';
  // 正常计算转换后的结果
  if (isPositiveNum(String(num)) || num === 0) {
    if (num === 0) {
      res = '0';
    } else {
      const basics = '100';
      let fillZero = '';
      for (let i = 0;i < decimal;i++) {
        fillZero += '0';
      }
      const tempRes = String(
        Math?.round(num * Number(basics + fillZero)) / Number(`1${fillZero}`),
      );
      const isInteger = tempRes?.indexOf('.') === -1;
      // 补零
      if (decimal > 0) {
        if (isInteger) {
          // 是整数直接补0
          res = `${tempRes}.${''?.padEnd(decimal, '0')}%`;
        } else {
          // 不是整数
          res = `${tempRes?.split('.')?.[0]}.${tempRes
            ?.split('.')?.[1]
            ?.padEnd(decimal, '0')}%`;
        }
      } else {
        res = isInteger ? `${tempRes}%` : `${tempRes?.split('.')?.[0]}%`;
      }
    }
  }
  const noEndZeroVal = parseFloat(res);
  if (!isNaN(noEndZeroVal) && noEndZeroVal >= 100000) {
    // 超出最大限制时
    res = `99999${decimal > 0 ? `.${''?.padEnd(decimal, '9')}` : ''}%+`;
  } else if (!isNaN(noEndZeroVal) && !isEndZero) {
    // 不保留无效末尾的0时
    const endStr = res?.slice(-1);
    res = noEndZeroVal + (endStr == '%' ? endStr : '');
  }
  return res;
};

// 支持正数、0、负数
export const getFormatPercentPlus = (
  v: string | number,
  decimal = 0, // 保留整数
  isEndZero = true,
) => {
  const isValid = isValidNumber(v);
  if (!isValid) return '-';
  let valueTemp: any = Number(v);
  let isNegativeNum = false;
  isNegativeNum = valueTemp < 0;
  valueTemp = getFormatPercent(Math.abs(valueTemp), decimal, isEndZero);
  return isNegativeNum ? `-${valueTemp}` : valueTemp;
};

// 计算出放大后的数字
const getTempRes = (data: number, splitNum: number, symbolNum = 1) => {
  if (data % (splitNum * symbolNum) === 0) {
    return data;
  }
  return Math?.ceil(data / (splitNum * symbolNum)) * splitNum * symbolNum;
};

// 判断放大后的数字转换单位后是否与原来单位一致，不一致的话需要继续递归放大，找到合适的数字
const getRes = (flag: boolean, data: number, splitNum: number) => {
  if (flag) {
    return data;
  }
  return getSuitMaxNumber(data, splitNum);
};

/**
 * 把一个数字分割成指定的段数，能够完整的分割，并且分割后每个刻度都是整数显示，找到这个数对应放大后的数
 * @param data 原始数据
 * @param splitNum 分割成多少段
 * @param isFormatNum 是否需要将数据转换B/M/K单位计算
 * @returns 之后放大后的数据
 */
export const getSuitMaxNumber = (
  data: number,
  splitNum: number,
  isFormatNum = true,
): number => {
  let res = data;
  let tempRes = data;
  const symbol = isFormatNum ? getFormatNum(data)?.slice(-1) : data;
  switch (symbol) {
    case 'K':
      tempRes = getTempRes(data, splitNum, 1000);
      res = getRes(getFormatNum(tempRes)?.slice(-1) === 'K', tempRes, splitNum);
      break;

    case 'M':
      tempRes = getTempRes(data, splitNum, 1000000);
      res = getRes(getFormatNum(tempRes)?.slice(-1) === 'M', tempRes, splitNum);
      break;

    case 'B':
      tempRes = getTempRes(data, splitNum, 1000000000);
      res = getRes(getFormatNum(tempRes)?.slice(-1) === 'B', tempRes, splitNum);
      break;

    default:
      tempRes = getTempRes(data, splitNum);
      res = getRes(
        !['B', 'M', 'K']?.includes(getFormatNum(tempRes)?.slice(-1)),
        tempRes,
        splitNum,
      );
      break;
  }

  return res;
};

/**
 * 把一维数组分割成二维数组
 * @param val 原始数据
 * @param interval 分割间隔
 * return 结果数据
 */
export const splitList = (val: any[], interval: number) => {
  const data: any = [];
  function fn(v: any[]) {
    if (v?.length > interval) {
      data.push(v?.slice(0, interval));
      v?.splice(0, interval);
      fn(v);
    } else {
      if (v?.length > 0) {
        data.push(v);
      } else {
        return;
      }
    }
  }
  fn(val);
  return data;
};

/**
 * 页面实时刷新/定时器
 * @param {*} fn 要执行的操作
 * @param {number} interval 时间间隔（分）
 * @param {'interval' | 'timeout'} type 定时器类型
 * @returns 定时器对象
 */
export const realTimeRefresh = (
  fn: () => void,
  interval: number,
  type: 'interval' | 'timeout' = 'interval',
) => {
  if (type === 'interval') {
    return setInterval(() => fn(), interval * 1000);
    // return setInterval(() => fn(), interval * 60 * 1000);
  }
  if (type === 'timeout') {
    return setTimeout(() => fn(), interval * 1000);
    // return setTimeout(() => fn(), interval * 60 * 1000);
  }
};

// 将内容转换成有效的数字
export function vaildNum(value: any) {
  if (value === '.' || !value) return '0';

  const beforeZero = value?.slice(0, 1) === '0' && value?.slice(1, 2) !== '.'; // 首位输入的是 0且后面没有小数点

  value = value.replace(/[^\d.]/g, ''); // 清除“数字”和“.”以外的字符

  value = value.replace(/\.{2,}/g, '.'); // 只保留第一个. 清除多余的

  value = value.replace('.', '$#$').replace(/\./g, '').replace('$#$', '.');

  // value = value.replace(/^(\-)*(\d+)\.(\d\d).*$/,'$1$2.$3'); //只能输入两位小数

  const res = parseFloat(value)?.toString() === 'NaN' ? '0' : value;

  return beforeZero ? (res?.slice(1) ? res?.slice(1) : '0') : res;
}

export const loadVideoFromFile = (file: File): Promise<HTMLVideoElement> => {
  return new Promise((resolve, reject) => {
    const videoEl = document.createElement('video');
    const src = window.URL.createObjectURL(file);

    videoEl.addEventListener(
      'loadeddata',
      () => {
        window.URL.revokeObjectURL(src);
        resolve(videoEl);
      },
      false,
    );

    videoEl.addEventListener(
      'error',
      (e) => {
        window.URL.revokeObjectURL(src);
        reject(e);
      },
      false,
    );

    videoEl.setAttribute('src', src);
  });
};

export const loadImageFromFile = (file: File): Promise<HTMLImageElement> => {
  return new Promise((resolve, reject) => {
    const imageEl = document.createElement('img');
    const src = window.URL.createObjectURL(file);

    imageEl.addEventListener(
      'load',
      () => {
        window.URL.revokeObjectURL(src);
        resolve(imageEl);
      },
      false,
    );

    imageEl.addEventListener(
      'error',
      (e) => {
        window.URL.revokeObjectURL(src);
        reject(e);
      },
      false,
    );

    imageEl.setAttribute('src', src);
  });
};

/**
 * 计算文件大小
 * @param v 文件大小数字
 * @returns 转换单位后的结果
 */
export const getFileSize = (v: number) => {
  if (!v) return '';
  const g = Math.round(v / (1024 * 1024 * 1024));
  const m = Math.round(v / (1024 * 1024));
  const k = Math.round(v / 1024);
  if (g > 1) return `${g}G`;
  if (m > 1) return `${m}MB`;
  return `${k}K`;
};

/**
 * 格式化文件大小
 * @param v 文件大小，单位字节
 * @param placeholder 无效数据时，返回的字符串
 * 格式化后的文件大小展现形式
 */
export const formatFileSize = (v?: any, placeholder = '-'): string => {
  const vNum = Number.parseFloat(v);
  if (Number.isNaN(vNum)) return placeholder;
  if (vNum < 1024) return vNum.toString(10);
  return getFileSize(vNum);
};

const KB_RATIO = 2 ** 10;
const MB_RATIO = 2 ** 20;
const GB_RATIO = 2 ** 30;
const FILE_SIZE_UNITS_RATIOS: Record<string, number> = {
  k: KB_RATIO,
  m: MB_RATIO,
  g: GB_RATIO,
  kb: KB_RATIO,
  mb: MB_RATIO,
  gb: GB_RATIO,
  K: KB_RATIO,
  M: MB_RATIO,
  G: GB_RATIO,
  KB: KB_RATIO,
  MB: MB_RATIO,
  GB: GB_RATIO,
};

export const parseFileSize = (v?: any): number => {
  if (typeof v !== 'string') return Number.NaN;
  const vTrim = v.trim();
  if (!vTrim) return Number.NaN;
  const sizeNum = Number.parseFloat(vTrim);
  // 字节单位字符
  const sizeUnit = vTrim.match(/[a-zA-Z]+/)?.[0] || '';
  // 字节单位进制
  const sizeUnitRatio = FILE_SIZE_UNITS_RATIOS[sizeUnit] || 1;
  return sizeNum * sizeUnitRatio;
};

// 获取去除后缀的文件名及后缀
export function splitFileName(originalFileName: string): {
  fileName: string; // 没有后缀的文件名
  suffix: string; // 文件后缀，带.
  fileSuffixName: string; // 有文件后缀的文件名
} {
  const res = {
    fileName: '',
    suffix: '',
    fileSuffixName: '',
  };
  if (!originalFileName || typeof originalFileName !== 'string') return res;
  const i = originalFileName?.lastIndexOf('fileName='); // 是否是腾讯云路径名
  const startIndex = i > -1 ? i + 9 : 0;
  const fileName = originalFileName?.slice(startIndex); // 完整的文件名
  const dotIndex = fileName?.lastIndexOf('.');
  res.suffix = dotIndex > -1 ? fileName?.slice(dotIndex) : ''; // 文件后缀
  res.fileName = fileName?.slice(0, fileName?.length - res.suffix?.length); // 去除后缀的文件名
  res.fileSuffixName = fileName;

  return res;
}

/**
 * 成功或失败提示
 * @param type 提示类型
 * @param content 提示内容
 */
export const messageTips = (type: 'success' | 'error', content: any) => {
  if (type === 'success')
    message.success({
      content,
      style: {
        backgroundColor: '#e6f8ee',
        border: '1px solid #9ce4bc',
        height: 44,
        color: '#007E3B',
      },
    });

  if (type === 'error')
    message.error({
      content,
      style: {
        backgroundColor: '#fcecec',
        border: '1px solid #f6b5b5',
        height: 44,
        color: '#B43537',
      },
    });
};

export const notificationTips = (
  type: 'success' | 'error',
  content: any,
  duration = 5000,
  className: any = '',
) => {
  notification[type]?.({
    className: `dbg-notification ${className}`,
    style: { fontSize: 14 },
    unique: true,
    duration,
    description: content,
  });
};

// 将科学计数法的数字转换成正常数字展示
export function scientificToNumber(num: string) {
  const flag = isValidNumber(num);
  if (!flag) return '';
  num = String(num) || '';
  try {
    if (/\d+\.?\d*e[+-]*\d+/i?.test(num)) {
      const zero = '0';
      const parts = num?.toLowerCase()?.split('e') || [];
      const e: any = parts?.[1];
      let zeroLen = Math?.abs(e);
      const sign = e / zeroLen;
      const beforeArr = parts?.[0]?.split('.') || [];
      if (sign < 0) {
        if (Array.isArray(beforeArr)) {
          num = `${zero}.${new Array(zeroLen)?.join(zero)}${beforeArr?.join(
            '',
          )}`;
        }
      } else {
        const dec = beforeArr?.[1];
        if (dec && Array.isArray(beforeArr)) {
          zeroLen = zeroLen - dec?.length;
          num = beforeArr?.join('') + new Array(zeroLen + 1)?.join(zero);
        }
      }
    }
  } catch (error) {
    console.error(error);
  } finally {
    return num;
  }
}

// 判断输入是否是一个网址
export const isLegalHttpUrl = (str: string, requireProtocal = true) => {
  if (typeof str !== 'string') return false;
  // const reg=/^\b(((https?|ftp):\/\/)?[-a-z0-9]+(\.[-a-z0-9]+)*\.(?:com|edu|gov|int|mil|net|org|biz|info|name|museum|asia|coop|aero|[a-z][a-z]|((25[0-5])|(2[0-4]\d)|(1\d\d)|([1-9]\d)|\d))\b(\/[-a-z0-9_:\@&?=+,.!\/~%\$]*)?)$/;
  // 协议可选
  // const reg=/^(((ht|f)tps?):\/\/)?([^!@#$%^&*?.\s-]([^!@#$%^&*?.\s]{0,63}[^!@#$%^&*?.\s])?\.)+[a-z]{2,6}\/?/;
  // 协议必填
  // const reg=/^(((ht|f)tps?):\/\/)([^!@#$%^&*?.\s-]([^!@#$%^&*?.\s]{0,63}[^!@#$%^&*?.\s])?\.)+[a-z]{2,6}\/?/;

  const reg = requireProtocal
    ? /^(((ht|f)tps?):\/\/)([^!@#$%^&*?.\s-]([^!@#$%^&*?.\s]{0,63}[^!@#$%^&*?.\s])?\.)+[a-z]{2,6}\/?/
    : /^(((ht|f)tps?):\/\/)?([^!@#$%^&*?.\s-]([^!@#$%^&*?.\s]{0,63}[^!@#$%^&*?.\s])?\.)+[a-z]{2,6}\/?/;
  return reg.test(str?.trim());
};

// 修改下拉选择搜索为空时的展示文案
export const changeDropDownListOnSearchEmptyText = (
  isEmpty: boolean,
  content = CONSTANT_TEXTS.EMPTY_TEXT,
) => {
  if (isEmpty) {
    let i = 0;
    const timer = setInterval(() => {
      // eslint-disable-next-line no-plusplus
      ++i;
      if (i > 40) {
        clearInterval(timer);
      } else {
        const el: any = document.querySelector(
          '.tea-overlay .globalpedia-filter-search-list-empty .tea-action-state__text',
        );
        if (el) {
          el.removeAttribute('title');
          el.innerHTML = content;
          clearInterval(timer);
        }
      }
    }, 30);
  }
};

// Select、SelectMultiple筛选器 的默认 filter 过滤逻辑
export const defaultSelectFilter: ISelectFilter = (keyword, option) => {
  const { label } = option || {};
  const keywordTemp = keyword?.toLowerCase() || '';
  const labelTemp = typeof label === 'string' ? label?.toLowerCase() : '';
  return labelTemp?.includes(keywordTemp);
};

const getStdTexts = (
  option?: IGPSelectOptionWithGroup | null,
  searchValue?: string,
  isIgnoreCase?: boolean,
) => {
  const optionLabel = option?.label;
  const optionLabelTmp = !!isIgnoreCase
    ? optionLabel?.toLowerCase()
    : optionLabel;
  const searchValueTmp = !!isIgnoreCase
    ? searchValue?.toLowerCase()
    : searchValue;
  return {
    stdOptionLabel: optionLabelTmp || '',
    stdSearchValue: searchValueTmp || '',
  };
};

export const selectFilter = (
  option?: IGPSelectOptionWithGroup,
  searchValue?: string,
  // 筛选规则配置
  filterCfg?: {
    // 是否拆分成单个字符 判断是否需要高亮
    isSeparate?: boolean;
    // 是否忽略大小写 判断是否高亮
    isIgnoreCase?: boolean;
  },
): boolean => {
  if (!searchValue) return true;
  const { isSeparate = true, isIgnoreCase = true } = filterCfg || {};
  const { stdOptionLabel, stdSearchValue } = getStdTexts(
    option,
    searchValue,
    isIgnoreCase,
  );

  // 不拆分字符匹配
  if (!isSeparate) return !!stdOptionLabel?.includes(stdSearchValue);

  // 拆分字符匹配
  return !!stdSearchValue?.split('')?.every((char) => {
    return !!stdOptionLabel?.includes(char);
  });
};

const chars = [
  // eslint-disable-next-line prettier/prettier
  '0', '1', '2', '3', '4', '5', '6', '7', '8', '9',
  // eslint-disable-next-line prettier/prettier
  'a', 'b', 'c', 'd', 'e', 'f', 'g', 'h', 'i', 'j', 'k', 'l', 'm', 'n', 'o', 'p', 'q', 'r', 's', 't', 'u', 'v', 'w', 'x', 'y', 'z',
  // eslint-disable-next-line prettier/prettier
  'A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L', 'M', 'N', 'O', 'P', 'Q', 'R', 'S', 'T', 'U', 'V', 'W', 'X', 'Y', 'Z',
];

export const generateRandomString = (length = 6): string => {
  const map = new Map<string, boolean>();
  const total = chars.length;
  let result = '';
  while (result.length < length) {
    const randomIndex = Math.floor(Math.random() * total);
    const randomChar = chars[randomIndex];
    if (!map.get(randomChar)) {
      result += randomChar;
      map.set(randomChar, true);
    }
  }
  return result;
};

export const getUniqueKey = () => {
  const randomString = generateRandomString(6);
  const timestamp = Date.now();
  return `${randomString}-${timestamp}`;
};

const UNIT_MAP: any = {
  day: {
    single: 'Day',
    multiple: 'Days',
  },
  week: {
    single: 'Week',
    multiple: 'Weeks',
  },
  month: {
    single: 'Month',
    multiple: 'Months',
  },
  quarter: {
    single: 'Quarter',
    multiple: 'Quarters',
  },
};

export const getUnitByCount = (
  baseUnit: string,
  count: number,
  unitMap = UNIT_MAP,
): string => {
  if (!baseUnit) return '';
  if (Number.isNaN(count)) return baseUnit;
  const unitData = unitMap?.[baseUnit];
  if (!unitData) return baseUnit;

  const result = count > 1 ? unitData.multiple : unitData.single;
  return result || baseUnit || '';
};

/**
 * 指定字符串 转换为首字符大写
 * @param str 原字符串
 * @param firstEndLowerCase 从第二个字符开始剩余的字符，是否转换为小写
 * @param allTranslation 是否全部单词转换
 * @returns 转换后的字符串
 */
export const toFirstUpperCase = (
  str: string,
  firstEndLowerCase = false,
  allTranslation = true,
): string => {
  if (typeof str !== 'string') return '';
  if (allTranslation) {
    try {
      return str
        ?.split(' ')
        ?.map((t: string) => {
          return (
            t.substring(0, 1).toUpperCase() +
            (firstEndLowerCase ? t.substring(1).toLowerCase() : t.substring(1))
          );
        })
        ?.join(' ');
    } catch (error) {
      return str;
    }
  } else {
    const firstLetter = str.substring(0, 1).toUpperCase();
    const remainsLetters = firstEndLowerCase
      ? str.substring(1).toLowerCase()
      : str.substring(1);
    return firstLetter + remainsLetters;
  }
};

export const round = (num: number, decimal = 2): number => {
  const _num = Number(num);
  let _decimal = Number(decimal);
  if (Number.isNaN(_num)) return 0;

  _decimal = Number.isNaN(_decimal) ? 2 : _decimal;
  const temp = Math.pow(10, _decimal);
  return Math.round(_num * temp) / temp;
};

const getValidFractionDigits = (fractionDigits?: any): number => {
  const fractionDigitsTmp = Number.parseInt(fractionDigits, 10);
  return Number.isNaN(fractionDigitsTmp) ? 0 : fractionDigitsTmp;
};

export const mathRound = (
  num?: number | any,
  fractionDigits = 2,
  placeholder = '-',
): number | string => {
  const numTmp = Number.parseFloat(num);
  if (Number.isNaN(numTmp)) return placeholder;
  const fractionDigitsTmp = getValidFractionDigits(fractionDigits);
  const numCache = 10 ** fractionDigitsTmp;
  return Math.round(numTmp * numCache) / numCache;
};

export const roundWithFixedDecimal = (num: number, decimal = 2): string => {
  let _decimal = Number(decimal);
  _decimal = Number.isNaN(_decimal) ? 2 : _decimal;
  const roundedNum = round(num, _decimal);
  return roundedNum?.toFixed(_decimal);
};

export const getPureTextFromRichText = (str: string): string => {
  if (typeof str !== 'string') return str;
  const reg = /<[^>]+>/gim;
  return str.replace(reg, '');
};

export const getMathValue = (v?: any, isSupportedNaN = true): number => {
  const vNum = Number.parseFloat(v);
  // eslint-disable-next-line no-nested-ternary
  return Number.isNaN(vNum) ? (isSupportedNaN ? Number.NaN : 0) : vNum;
};

export const getElementStyleValue = (
  node: Element,
  propName: string,
): string | number => {
  if (!node) return '';
  return window.getComputedStyle(node, null).getPropertyValue(propName);
};

export const getElementStyleMathValue = (
  node: Element,
  propName: string,
): number => {
  let styleValue = getElementStyleValue(node, propName);
  // @ts-ignore
  styleValue = Number.parseFloat(styleValue);
  return Number.isNaN(styleValue) ? 0 : round(styleValue);
};

/**
 * 判断地址栏是否有效
 * 第一版 是从url获取到filter查询参数后，代码里先解码，再转为JSON对象，但是在 filter中包含 URI特殊字符的时候，会直接报错
 * 所以改为第二版，在第一版的基础上catch到异常后，再次尝试不用代码解码，直接转JSON对象（可能是umi路由获取查询参数时，umi路由已经做过解码，避免二次解码导致异常）
 * 在第二层解析 还是报错，就走原有逻辑，跳转到自定的页面
 * @param callBackPath 无效时跳转到的页面地址
 * @returns 返回的有效地址栏参数对象
 */
export const getLocationIsValid = (callBackPath = ''): object => {
  const filter: string =
    ((history.location.query as any).filter as string) || '';
  let filterObj = {};
  try {
    filterObj = filter ? JSON.parse(decodeURIComponent(filter)) : {};
  } catch (error) {
    try {
      filterObj = filter ? JSON.parse(filter) : {};
    } catch (error) {
      // 如果上一页有内容就返回上一页，没有就跳转到兜底页面
      if (history?.length > 2) {
        history.goBack();
      } else {
        callBackPath &&
          history.replace(
            callBackPath?.includes(SLASH_G_POP)
              ? callBackPath
              : SLASH_G_POP + callBackPath,
          );
      }
      messageTips('error', 'Invalid Location');
      console.error(error);
    }
  }

  return filterObj;
};

export const downloadFileByLinker = (url: string, fileName: string) => {
  const linker = document.createElement('a');
  linker.setAttribute('href', url);
  linker.setAttribute('download', fileName);
  linker.style.display = 'none';
  document.body.appendChild(linker);
  linker.click();
  document.body.removeChild(linker);
};

export const downloadFileByBlob = (
  fileStream: BlobPart,
  fileName = 'download',
  fileType = 'text/csv',
  charset = 'utf-8',
) => {
  const blob = new Blob([fileStream], { type: `${fileType},${charset}` });
  const url = window.URL.createObjectURL(blob);
  try {
    downloadFileByLinker(url, fileName);
  } catch (exception) {
    console.warn(exception);
  } finally {
    window.URL.revokeObjectURL(url);
  }
};

export const stream2Object = (
  stream: BlobPart,
  type = 'text/plain',
  charset = 'utf-8',
): any => {
  return new Promise((resolve, reject) => {
    const blob = new Blob([stream], { type });
    const reader = new FileReader();
    reader.readAsText(blob, charset);
    reader.onload = () => {
      let result;
      try {
        result = JSON.parse(reader.result as string);
      } catch (exception) {
        console.warn(exception);
        result = {};
      }
      resolve(result);
    };

    reader.onerror = (exception) => {
      reject(exception);
    };
  });
};

// 获取下载CSV时接口返回流中的文件名称 headers: 响应头信息
export const getDownLoadCSVResponseHeaderFileName = (headers: any): string => {
  const { 'content-disposition': contentDisposition = '' } = headers || {};
  let fileName = contentDisposition?.split(';')[1] || '';
  fileName = fileName?.split('=')[1] || '';
  fileName = fileName?.trim()?.replace(/['"]/g, '') || '';
  try {
    fileName = decodeURIComponent(fileName);
  } catch (error: any) {
    throw new Error(error);
  } finally {
    return fileName;
  }
};

/**
 * 获取progress标签 指定 progress状态、delay延期数 的 hover气泡提示内容
 * @param progress progress状态
 * @param delay 延期的时间（固定月为单位，正数代表延期，负数代表提前）
 * @returns progress标签hover气泡提示内容
 */
export const getProgressTagHoverMsg = (
  progress: string,
  _delay: number,
): string | undefined => {
  // const unit = getUnitByCount('month', Math?.abs(delay))?.toLowerCase();
  // const delayText = `${Math?.abs(delay)} ${unit}`;
  if (['1', 1].includes(progress)) {
    // On Track
    return /* delay !== 0 ? `${delayText} ${delay > 0 ? 'delay' : 'in advance'}` : '' */ undefined;
  }
  if (['4', 4].includes(progress)) {
    // On Hold：固定的气泡信息：Project is paused. Expect further updates.
    return 'Project is paused. Expect further updates.';
  }
  if (['3', 3].includes(progress)) {
    // Monitor：Team is managing the risk.  + Delay日期
    return /* delay > 0 ? `Team is managing the risk. ${delayText} delay` :  */ 'Team is managing the risk.';
  }
  if (['2', 2].includes(progress)) {
    // At Risk：Team needs help. + Delay日期
    return /* delay > 0 ? `Team needs help. ${delayText} delay` :  */ 'Team needs help.';
  }
  return undefined;
};

/**
 * 判断一个对象是否全部无数据，包括每项为'-'，但不包括每项为字符串0
 * @param obj 对象或者极值数据
 * @returns boolean 是否全部无数据
 */
export const getObjNoData = (obj: object | any): boolean => {
  if (
    !obj ||
    Object.prototype.toString.call(obj) !== '[object Object]' ||
    obj === '-'
  )
    return true;
  if (Object.prototype.toString.call(obj) === '[object Object]') {
    for (const key in obj) {
      const v = (obj as { [key: string]: any; })?.[key];
      if ((!!v || v === 0) && v !== '-') {
        return false;
      }
    }
    return true;
  }
  return true;
};

export const getFileAndSuffixByFileName = (
  fileName?: string,
): { name: string; format: string; } => {
  if (!fileName) return { name: '', format: '' };
  const dotIdx = fileName.lastIndexOf('.');
  return {
    name: fileName.substring(0, dotIdx),
    format: fileName.substring(dotIdx + 1),
  };
};

export const formatMaterials2Files = (materials?: any[] | null): IFile[] => {
  if (!Array.isArray(materials) || !materials.length) return [];
  return materials.map((material) => {
    const {
      name: fileName,
      size,
      path,
      file_size: fileSize,
      cloud_path: cloudPath,
    } = material || {};
    const { name, format } = getFileAndSuffixByFileName(fileName);
    return {
      name,
      format,
      size: Number.parseFloat(size || fileSize),
      path: path || cloudPath,
    };
  });
};

export const getFileInfoByUrl = (
  fileUrl: string,
): {
  fileName: string;
  fileSuffix: string;
} => {
  if (typeof fileUrl !== 'string')
    return {
      fileName: '',
      fileSuffix: '',
    };

  let url = fileUrl;
  const searchParamsIdx = url.indexOf('?');
  if (searchParamsIdx !== -1) {
    url = url.substring(0, searchParamsIdx);
  }
  const filePathStr = url.substring(url.lastIndexOf('/') + 1);
  const filePathDotIdx = filePathStr.lastIndexOf('.');
  const fileName = filePathStr.substring(0, filePathDotIdx);
  const fileSuffix = filePathStr.substring(filePathDotIdx + 1);

  return {
    fileName,
    fileSuffix: typeof fileSuffix === 'string' ? fileSuffix.toLowerCase() : '',
  };
};

export const getDataURLByImageObject = (
  image: HTMLImageElement,
  quality = 1,
): string => {
  if (!(image instanceof HTMLImageElement)) return '';
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  const imageInfo = getFileInfoByUrl(image.src);
  const imageType = imageInfo.fileSuffix || 'png';
  const { width, height } = image;
  canvas.setAttribute('width', `${width}px`);
  canvas.setAttribute('height', `${height}px`);
  canvas.setAttribute('crossOrigin', 'anonymous');
  ctx?.drawImage(image, 0, 0, width, height);
  return canvas.toDataURL(`image/${imageType}`, quality);
};

// value数量 等于 可选项数量时（即全选），处理value为空数组
export const getValuesSpecial = <T,>(
  values: T[],
  options: ISelectOptionWithGroup[],
): T[] => {
  if (!Array.isArray(values)) return [];
  if (values.length === options?.length) return [];
  // @ts-ignore
  if (values?.includes('all')) return [];
  return values;
};

export const getOptionsValues = (data: ISelectOptionWithGroup[]): any[] => {
  const result: string[] = [];
  if (!Array.isArray(data)) return result;
  for (const item of data) {
    const value = item?.value;
    if (!value) continue;
    result.push(value);
  }
  return result;
};

export const getOptions = (
  data: any[],
  config: {
    textFieldName: string;
    valueFieldName: string;
  },
): ISelectOption[] => {
  const result: ISelectOption[] = [];
  if (!Array.isArray(data)) return result;
  const textFieldName = config?.textFieldName || 'text';
  const valueFieldName = config?.valueFieldName || 'value';
  for (const item of data) {
    const text = item?.[textFieldName];
    const value = item?.[valueFieldName];
    if (isEmpty(text) || isEmpty(value)) continue;
    result.push({ text, value });
  }
  return result;
};

/**
 * 给下拉框组件options增加分组key
 * @param options 下拉框组件Options
 * @param groupKey 分组的key
 * @returns 带有分组key的Options
 */
export function getOptionsWithSeparatedGroupKey<T>(
  options: T[],
  groupKey = 'separated',
): T[] {
  if (!Array.isArray(options)) return [];
  return options?.map((item) => ({ ...item, groupKey }));
}

// 字母排序规则
function letterSort(
  a: any,
  b: any,
  key = '',
  currentSortIndex: -1 | 1,
): number {
  const A = key ? a?.[key]?.toUpperCase() : a?.toUpperCase();
  const B = key ? b?.[key]?.toUpperCase() : b?.toUpperCase();
  if (A < B) {
    return -currentSortIndex;
  }
  if (A > B) {
    return currentSortIndex;
  }
  return 0;
}

// 数字排序规则
function numberSort(
  a: any,
  b: any,
  key = '',
  currentSortIndex: -1 | 1,
  suspenseSort = false,
  suspensePattern: 'string' | 'number' = 'number',
  suspenseKey = '',
): number {
  const aVal = a?.[key] || a;
  const bVal = b?.[key] || b;
  const aNum = Number.parseFloat(aVal);
  const bNum = Number.parseFloat(bVal);
  if (!Number.isNaN(aNum) && !Number.isNaN(bNum)) {
    if (aNum < bNum) {
      return -currentSortIndex;
    }
    if (aNum > bNum) {
      return currentSortIndex;
    }
    return 0;
  }
  // 都是无效数据及都为0时的排序规则
  if (
    (Number.isNaN(aNum) && Number.isNaN(bNum)) ||
    (aNum === 0 && bNum === 0)
  ) {
    if (!suspenseSort) return 0;
    // 如果是数字排序
    if (suspensePattern === 'number') {
      return numberSort(a, b, suspenseKey, currentSortIndex, false);
    }

    // 如果是字符串排序
    return letterSort(a, b, suspenseKey, currentSortIndex);
  }
  return Number.isNaN(aNum) ? -currentSortIndex : currentSortIndex;
}

/**
 * 将数组按某个字段排序
 * @param originalArr 原数组
 * @param pattern 字符串排序还是数字排序
 * @param sortType 排序规则: 升序asc排序（或A-Z排序）/降序desc排序（或Z-A排序）
 * @param key 遵循哪个字段排序
 * @param suspenseSort 两个都是无效数据时是否按其它字段排序（注：数字排序时，两个都为0时也准从这个备用排序）
 * @param suspensePattern 两个都是无效数据时的备用排序模式
 * @param suspenseKey 两个都是无效数据时的备用排序字段
 * @returns 排序后的数组
 */
export const getArraySort = (
  originalArr: any[],
  pattern: 'string' | 'number' = 'number',
  sortType: 'asc' | 'desc' = 'asc',
  key = '',
  suspenseSort = false,
  suspensePattern: 'string' | 'number' = 'number',
  suspenseKey = '',
): any[] => {
  if (!Array.isArray(originalArr)) return originalArr;
  const sortArr = originalArr?.sort((a, b) => {
    // 区分asc或desc排序
    const currentSortIndex = sortType === 'desc' ? -1 : 1;

    // 如果是数字排序
    if (pattern === 'number') {
      return numberSort(
        a,
        b,
        key,
        currentSortIndex,
        suspenseSort,
        suspensePattern,
        suspenseKey,
      );
    }

    // 如果是字符串排序
    return letterSort(a, b, key, currentSortIndex);
  });

  return sortArr;
};

export const generateDateComparator = <T extends Record<string, any> = {}>(
  dateKey = 'date',
  mode: ISortOrder = 'asc',
): ((a: T, b: T) => number) => {
  const modeNum = mode === 'asc' ? 1 : -1;
  return (a, b): number => {
    const aDate = a?.[dateKey];
    const bDate = b?.[dateKey];
    const aDateM = aDate ? moment(aDate) : null;
    const bDateM = bDate ? moment(bDate) : null;
    const aDateMValid = aDateM?.isValid() || false;
    const bDateMValid = bDateM?.isValid() || false;
    if (!aDateMValid && !bDateMValid) return 0;
    if (!aDateMValid) return 1;
    if (!bDateMValid) return -1;
    if (aDateM?.isSame(bDateM)) return 0;
    return aDateM?.isAfter(bDateM) ? modeNum : -modeNum;
  };
};

export const getSortedByDate = <T extends IRecord>(
  list: T[],
  dateKey = 'date',
  mode: ISortOrder = 'asc',
) => {
  if (!Array.isArray(list) || !list.length) return [];
  const comparator = generateDateComparator(dateKey, mode);
  const sortedList = cloneDeep(list);
  sortedList.sort(comparator);
  return sortedList;
};

/**
 * 时间转换
 * @param date
 * @param pattern 转换的格式
 * @return string
 */
export const getValidDate = (
  date: string,
  pattern: string,
  placeholder: any = '-',
): string => {
  return moment(date).isValid() && date
    ? moment(date).format(pattern)
    : date || placeholder;
};

export const dispatchResizeEvent = () => {
  // 手动触发窗口resize事件
  if (document.createEvent) {
    const event = document.createEvent('HTMLEvents');
    event.initEvent('resize', true, true);
    window.dispatchEvent(event);
    // @ts-ignore
  } else if (document.createEventObject) {
    // @ts-ignore
    window.fireEvent('onresize');
  }
};

// 懒加载添加数据
export const lazyLoadingList = (
  totalList: any[], // 总数据
  currentList: any[], // 当前展示数据
  initLength: number, // 初始加载多少条数据
  loadingType: 1 | 2, // 去删除数据还是添加数据 1：添加，2：删除
  loadingCount: number, // 每次添加多少条数据
): any[] => {
  if (!Array.isArray(totalList) || !Array.isArray(currentList)) return [];
  const totalLen = totalList?.length || 0;
  const currentLen = currentList?.length || 0;
  if (loadingType === 1) {
    // 初始化
    if (currentLen < initLength) {
      return totalList?.slice(0, initLength);
    }
    // 正常加数据
    if (currentLen + loadingCount <= totalLen) {
      return totalList?.slice(0, currentLen + loadingCount);
    }
    // 加载全部数据
    return totalList;
  }
  if (loadingType === 2) {
    // 可以减
    if (currentLen > loadingCount) {
      return currentList?.slice(0, currentLen - loadingCount);
    }
    // 减不了
    return currentList?.slice(0, initLength);
  }
  return [];
};

/**
 * 用真实数据替换模板字符串，用数据源对象中的每一个key值，作为模板字符串中的占位字符 去匹配替换
 * @param template 模板字符串
 * @param data 数据源
 * @returns 替换真实数据的字符串
 */
export const replaceTemplateStr = (
  template: string,
  data: { [key: string]: any; },
): string => {
  if (typeof template !== 'string') return '';
  let str = template;
  Object.entries(data).forEach(([key, value]) => {
    const keyReg = new RegExp(`\\$\\{${key}\\}`, 'g');
    str = str.replace(keyReg, value);
  });
  return str;
};

/**
 * 获取指定日期所在月份的第一天
 * @param date 日期字符串
 * @returns 指定日期所在月份的第一天
 */
export const getFirstDayOfMonth = (date?: string): string => {
  if (!date?.trim()) return '';
  const m = moment(date);
  if (!m.isValid()) return '';
  return m.startOf('month').format('YYYY-MM-DD');
};

/**
 * 获取指定日期所在月份的最后一天
 * @param date 日期字符串
 * @returns 指定日期所在月份的最后一天
 */
export const getLastDayOfMonth = (date?: string): string => {
  if (!date?.trim()) return '';
  const m = moment(date);
  if (!m.isValid()) return '';
  return m.endOf('month').format('YYYY-MM-DD');
};

/**
 * 获取指定日期所在年份的第一天
 * @param date 日期字符串
 * @returns 指定日期所在年份的第一天
 */
export const getFirstDayOfYear = (date?: string): string => {
  if (!date?.trim()) return '';
  const m = moment(date);
  if (!m.isValid()) return '';
  return m.startOf('year').format('YYYY-MM-DD');
};

/**
 * 获取指定日期所在年份的最后一天
 * @param date 日期字符串
 * @returns 指定日期所在年份的最后一天
 */
export const getLastDayOfYear = (date?: string): string => {
  if (!date?.trim()) return '';
  const m = moment(date);
  if (!m.isValid()) return '';
  return m.endOf('year').format('YYYY-MM-DD');
};

export const isSafari = () => {
  const ua = navigator.userAgent.toLowerCase();
  return /safari/.test(ua) && !/chrome/.test(ua);
};

export const openTabPage = (url?: string) => {
  if (!url) return;
  if (isSafari()) {
    const page = window.open('', '_blank');
    if (page) {
      page.location = url;
    }
  } else {
    window.open(url);
  }
};

/**
 * 判断指定文件后缀 是否为图片
 * @param suffix 文件后缀
 * @returns 指定指定文件后缀 是否为 图片资源
 */
export const isImageWithSuffix = (suffix: string): boolean => {
  return IMAGE_SUFFIXES?.includes(suffix);
};

/**
 * 判断指定文件后缀 是否为视频
 * @param suffix 文件后缀
 * @returns 指定指定文件后缀 是否为 视频资源
 */
export const isVideoWithSuffix = (suffix: string): boolean => {
  return VIDEO_SUFFIXES?.includes(suffix);
};

/**
 * 判断指定文件名、或文件路径的 文件 是否为图片，根据文件后缀进行判断
 * @param fileName 文件名，也可以是文件路径，只需要包含文件后缀即可
 * @returns 指定文件名、或路径的文件 是否为 图片资源
 */
export const isImage = (fileName: string): boolean => {
  const { fileSuffix } = getFileInfoByUrl(fileName);
  return isImageWithSuffix(fileSuffix);
};

/**
 * 判断腾讯云COS文档预览插件 是否支持该文件类型
 * 用于studio overall详情页 management页签的 董事会议模块 附件预览
 * @param suffix 文件后缀
 * @returns 腾讯云COS文档预览插件 是否支持该文件类型
 */
export const isFilePreviewableByCOSWithSuffix = (suffix: string): boolean => {
  return COS_PREVIEWABLE_FILE_SUFFIXES?.includes(suffix);
};

/**
 * 判断腾讯云COS文档预览插件 是否支持该文件类型,添加视频
 * 用于studio overall详情页 management页签的 董事会议模块 附件预览
 * @param suffix 文件后缀
 * @returns 腾讯云COS文档预览插件 是否支持该文件类型
 */
export const isFilePreviewableByCOSWithSuffixAddVideo = (
  suffix: string,
): boolean => {
  return COS_PREVIEWABLE_FILE_SUFFIXES_ADD_VIDEO?.includes(suffix);
};

/**
 * 判断腾讯云COS文档预览插件 是否支持该文件类型
 * 用于studio overall详情页 management页签的 董事会议模块 附件预览
 * @param fileName 文件名称
 * @returns 腾讯云COS文档预览插件 是否支持该文件类型
 */
export const isFilePreviewableByCOS = (fileName: string): boolean => {
  const { fileSuffix } = getFileInfoByUrl(fileName);
  return isFilePreviewableByCOSWithSuffix(fileSuffix);
};

/**
 * 返回指定文件是否可以预览，包含腾讯云COS文档预览插件、浏览器直接打开两种方式
 * 用于studio overall详情页 management页签的 董事会议模块 附件预览
 * @param suffix 文件后缀
 * @returns 返回该文件是否可以预览
 */
export const isFilePreviewableWithSuffix = (suffix: string): boolean => {
  return PREVIEWABLE_FILE_SUFFIXES?.includes(suffix);
};

/**
 * 返回指定文件是否可以预览，包含腾讯云COS文档预览插件、浏览器直接打开两种方式
 * 用于studio overall详情页 management页签的 董事会议模块 附件预览
 * @param fileName 文件名称
 * @returns 返回该文件是否可以预览
 */
export const isFilePreviewable = (fileName: string): boolean => {
  const { fileSuffix } = getFileInfoByUrl(fileName);
  return isFilePreviewableWithSuffix(fileSuffix);
};

/**
 * 返回指定文件是否可以下载，包含常用office文件、图片
 * 用于studio overall详情页 management页签的 董事会议模块 附件下载
 * @param suffix 文件后缀
 * @returns 返回该文件是否可以下载
 */
export const isFileDownloadableWithSuffix = (suffix: string): boolean => {
  return DOWNLOADABLE_FILE_SUFFIXES?.includes(suffix);
};

/**
 * 返回指定文件是否可以下载，包含常用office文件、图片
 * 用于studio overall详情页 management页签的 董事会议模块 附件下载
 * @param fileName 文件名称
 * @returns 返回该文件是否可以下载
 */
export const isFileDownloadable = (fileName: string): boolean => {
  const { fileSuffix } = getFileInfoByUrl(fileName);
  return isFileDownloadableWithSuffix(fileSuffix);
};

/**
 * 获取y轴 min、max、interval值（分左边y轴、右边y轴），用于处理双y轴 刻度线对齐的问题
 * 根据valuesOfLeftYAxis、valuesOfRightYAxis 的值的集合 和 splitNumber，计算 合适的 min、max、interval值
 * @param valuesOfLeftYAxis 左边y轴的数值列表（可以是多个serie的数值，就是所以体现在左边y轴的数值的集合）
 * @param valuesOfRightYAxis 右边y轴的数值列表（可以是多个serie的数值，就是所以体现在右边y轴的数值的集合）
 * @param splitNumber y轴刻度线分割段数，默认5
 * @returns y轴 min、max、interval值（分左边y轴、右边y轴）
 */
export const getDoubleYAxisSplitorConfig = (
  valuesOfLeftYAxis: number[],
  valuesOfRightYAxis: number[],
  splitNumber = 5,
): {
  left: {
    min: number | undefined;
    max: number | undefined;
    interval: number | undefined;
  };
  right: {
    min: number | undefined;
    max: number | undefined;
    interval: number | undefined;
  };
} => {
  if (
    !(Array.isArray(valuesOfLeftYAxis) && Array.isArray(valuesOfRightYAxis))
  ) {
    return {
      left: {
        min: undefined,
        max: undefined,
        interval: undefined,
      },
      right: {
        min: undefined,
        max: undefined,
        interval: undefined,
      },
    };
  }

  const getMaxValue = (nums: number[]): number => {
    const max = Math.max(...nums);
    return Math.ceil(max / 9.5) * 10;
  };

  const getMinValue = (nums: number[]): number => {
    const min = Math.min(...nums);
    return Math.floor(min / 10.5) * 10;
  };

  const minLeft = getMinValue(valuesOfLeftYAxis);
  const maxLeft = getMaxValue(valuesOfLeftYAxis);
  const minRight = getMinValue(valuesOfRightYAxis);
  const maxRight = getMaxValue(valuesOfRightYAxis);

  return {
    left: {
      min: minLeft,
      max: maxLeft,
      interval: (maxLeft - minLeft) / splitNumber,
    },
    right: {
      min: minRight,
      max: maxRight,
      interval: (maxRight - minRight) / splitNumber,
    },
  };
};

// 查询当前系统
export const queryUseSystem = () => {
  const p = navigator?.platform?.toLowerCase();

  return {
    win: p?.indexOf('win') >= 0,
    mac: p?.indexOf('mac') >= 0,
  };
};

// 查询不同系统下使用快捷键与否是否可以跳转
export const queryJumpable = (
  e: React.MouseEvent<Element, MouseEvent> | (any & {}),
): boolean => {
  const { win, mac } = queryUseSystem();
  if (win && e?.ctrlKey) return false;
  if (mac && e?.metaKey) return false;
  return true;
};

// 最新console平台
export const CONSOLE_PLATFORM = [
  'console',
  'ps4/pro',
  'xbox one',
  'xbox series x/s',
  'ps5',
  'switch2',
  'switch1',
];

// 判断新platfrom是否有console平台
export const isIncludeConsole = (platform: any): boolean => {
  const platformLowerCase =
    (Array.isArray(platform)
      ? platform.join(',')?.toLowerCase()
      : platform?.trim()?.toLowerCase()) || '';

  // 方法1：使用some()和includes()
  const exists = CONSOLE_PLATFORM.some((item) =>
    platformLowerCase.includes(item),
  );
  return exists;
};

// 从当前时间倒退多少个月，得到开始日期及结束日期
export const getLastMonthes = (last = 11): [string, string] => {
  const startDate = moment()
    .subtract(last + 1, 'months')
    .startOf('month')
    .format('YYYY-MM-DD');
  const endDate = moment()
    .subtract(1, 'months')
    .endOf('month')
    .format('YYYY-MM-DD');
  return [startDate, endDate];
};

// 历史数据risk的category映射
const HISTORY_RISK_CATEGORY_MAPPING: Record<string, string> = {
  Timeline: 'Production',
  Technological: 'Tech',
  Operational: 'Operation',
  Financial: 'Finance',
  'Competition/Market': 'Market Competition',
};
// 处理risk历史数据（映射category）
export const handleRisksCategory = (risks?: IRiskVO[]): any[] => {
  if (!Array.isArray(risks) || !risks.length) return [];
  return risks.map((risk) => {
    const { category } = risk || {};
    return {
      ...risk,
      category: HISTORY_RISK_CATEGORY_MAPPING[category] || category,
    };
  });
};

export const synchonizeProjectUpdateFieldsDiff = (projectUpdate?: any): any => {
  const updatesInfoContent = projectUpdate?.content;
  return {
    ...projectUpdate,
    content: {
      ...updatesInfoContent,
      risks: handleRisksCategory(updatesInfoContent?.new_risks),
    },
  };
};

export const getSpecialListValues = <T,>(
  values?: T[] | null,
  options?: any[] | null,
  key?: string,
): T[] => {
  if (!Array.isArray(values)) return [];
  if (!Array.isArray(options)) return values;
  if (key && key === 'project_id') {
    return values;
  }
  return values.length === options.length ? [] : values;
};

export const formatDateStr = (
  dateStr?: string,
  pattern = 'YYYY-MM-DD',
): string => {
  if (!dateStr) return '';
  const dateM = moment(dateStr);
  if (!dateM.isValid()) return '';
  try {
    return dateM.format(pattern);
  } catch (exception) {
    return '';
  }
};

export const formatDateStrByTimestamp = (
  timestamp?: number,
  pattern = 'YYYY-MM-DD',
  placeholder = '-',
): string => {
  if (!timestamp) return placeholder;
  const m = moment(timestamp);
  if (!m.isValid()) return placeholder;
  try {
    return m.format(pattern);
  } catch (exception) {
    return placeholder;
  }
};

export const formatDateTimeStrByTimestamp = (
  timestamp?: number,
  placeholder = '-',
): string => {
  if (!timestamp) return placeholder;
  const m = moment(timestamp);
  if (!m.isValid()) return placeholder;
  try {
    return m.format('YYYY-MM-DD HH:mm:ss');
  } catch (exception) {
    return placeholder;
  }
};

export const updateItemOfListByIndex = <T,>(
  list?: T[] | null,
  index?: number | null,
  item?: Partial<T> | null,
): T[] => {
  if (!Array.isArray(list) || !list.length) return [];
  if (index === undefined || index === null) return [...list];
  if (index < 0 || index > list.length - 1) return [...list];
  const oldItem = list[index];
  const newItem = { ...oldItem, ...item };
  return [...list.slice(0, index), newItem, ...list.slice(index + 1)];
};

export function base64toFile(datary: string, filename: string) {
  const arr = datary?.split(',');
  const mime = arr?.[0]?.match(/:(.*?);/)?.[1];
  const suffix = mime?.split('/')?.[1];
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);
  // eslint-disable-next-line no-plusplus
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }
  return new File([u8arr], `${filename}.${suffix}`, { type: mime });
}

export const formatTeaFormItemStatus = (
  showError: boolean,
  fieldMeta: FieldRenderProps['meta'],
): FormItemProps['status'] => {
  if (!showError) return 'success';
  const { touched, validating, valid } = fieldMeta || {};
  if (!touched) return 'success';
  if (validating) return 'validating';
  return valid ? 'success' : 'error';
};

export const formatComposedTeaFormItemStatus = (
  showError: boolean,
  fieldMetas: FieldRenderProps['meta'][],
): FormItemProps['status'] => {
  if (!showError) return 'success';
  if (!Array.isArray(fieldMetas) || !fieldMetas.length) return 'error';
  let hasError = false;
  let hasValidating = false;
  for (const fieldMeta of fieldMetas) {
    const { touched, validating, valid } = fieldMeta || {};
    if (touched && !valid) {
      hasError = true;
      break;
    }
    if (touched && validating) {
      hasValidating = true;
    }
  }
  if (hasError) return 'error';
  return hasValidating ? 'validating' : 'success';
};

export const formatTeaFormItemMessage = (
  showError: boolean,
  fieldMeta: FieldRenderProps['meta'],
): FormItemProps['message'] => {
  if (!showError) return '';
  const { touched, validating, valid, error } = fieldMeta || {};
  if (!touched) return '';
  if (validating) return '';
  return valid ? '' : error;
};

export const formatComposedTeaFormItemMessage = (
  showError: boolean,
  fieldMetas: FieldRenderProps['meta'][],
): FormItemProps['message'] => {
  if (!showError) return '';
  let message = '';
  for (const fieldMeta of fieldMetas) {
    const { touched, valid, error } = fieldMeta || {};
    if (touched && !valid) {
      message = error;
      break;
    }
  }
  return message;
};

export const getValidDateRange = (
  startDate?: string,
  endDate?: string,
): [string, string] | [] => {
  if (!startDate || !endDate) return [];
  const startDateM: moment.Moment | null = startDate ? moment(startDate) : null;
  const endDateM: moment.Moment | null = endDate ? moment(endDate) : null;
  if (!startDateM?.isValid() || !endDateM?.isValid()) return [];
  return startDateM.isSameOrBefore(endDateM)
    ? [startDate, endDate]
    : [endDate, startDate];
};

// 都保留两位小数
export function newMathFormat(
  data: any,
  showZero = false,
  fractionDigits = 2,
  forcePadEndZero = false,
) {
  let unit = 'k';
  let value: any = Math.abs(data);
  if (showZero && !data) return 0;
  if (parseFloat(data) === 0) return 0; // 数据为0就要展示0， 无数据展示-
  if (!data || isNaN(data)) return '-';
  const isNegativeStr = data < 0 ? '-' : '';
  // 最外面的 parseFloat用于去除末尾无效的零
  if (value < 1000000 && value >= 1000) {
    unit = 'K';
    value = parseFloat((parseFloat(value) / 1000).toFixed(fractionDigits));
  } else if (value >= 1000000 && value < 1000000000) {
    unit = 'M';
    value = parseFloat((parseFloat(value) / 1000000).toFixed(fractionDigits));
  } else if (value >= 1000000000) {
    unit = 'B';
    value = parseFloat(
      (parseFloat(value) / 1000000000).toFixed(fractionDigits),
    );
  } else if (value < 1000) {
    unit = '';
    value = parseFloat(parseFloat(value).toFixed(fractionDigits));
  }
  // return isNegativeStr + Math.abs(value) + unit;

  // Math.abs会去掉末尾的0，为保证数据格式统一，所以不用
  if (value < 0) value = -value;
  // 强制保留2位小数（不足补0）（0就不补充小数了）
  if (forcePadEndZero) value = getNumberWithFraction(value);
  return isNegativeStr + value + unit;
}

export const newMathFormatWrapper = (
  data: any,
  showZero = false,
  fractionDigits = 2,
  placeholder = '-',
) => {
  const dataNum = Number.parseFloat(data);
  if (Number.isNaN(dataNum)) return placeholder;
  return newMathFormat(dataNum, showZero, fractionDigits);
};

export const newMathFormatForcePadEndZeroWrapper = (
  data: any,
  showZero = false,
  fractionDigits = 2,
  placeholder = '-',
) => {
  const dataNum = Number.parseFloat(data);
  if (Number.isNaN(dataNum)) return placeholder;
  return newMathFormat(dataNum, showZero, fractionDigits, true);
};

const getNumberWithFraction = (num: number): string => {
  if (num === 0) return String(num);
  const numStr = String(num);
  const dotIndex = numStr.lastIndexOf('.');

  if (dotIndex === -1) return `${numStr}.00`;

  const numInteger = numStr.substring(0, dotIndex);
  const numFraction = numStr.substring(dotIndex + 1).padEnd(2, '0');
  return `${numInteger}.${numFraction}`;
};

export const getPureEmptyValue = (v: any): any => (v && v !== '-' ? v : '');

/**
 * 设置moment的locale配置的中week的配置，一般用来设置星期几作为一周的开始日期
 * @param cfg 配置
 * @return 恢复的方法（临时设置，实现业务功能后需要恢复到之前的配置）
 */
export const setLocaleWeekCfgOfMoment = (cfg: {
  dow?: number;
  doy?: number;
}) => {
  const { dow: dowCfg, doy: doyCfg } = cfg || {};
  const momentLocale = moment.locale();
  // @ts-ignore
  const currentMomentLocaleWeek = moment.localeData()?._week;
  const { dow, doy } = currentMomentLocaleWeek || {};
  // 临时设置星期一作为一周的开始日期
  moment.updateLocale(momentLocale, {
    week: {
      // 星期的第一天是星期一
      dow: dowCfg ?? dow,
      // 年份的第一周必须包含1月4日 (7 + 1 - 4)
      doy: doyCfg ?? doy,
    },
  });

  // 恢复临时设置前的配置
  return () => {
    moment.updateLocale(momentLocale, {
      week: {
        dow,
        doy,
      },
    });
  };
};

export const getProjectDetailsPagePath = (params?: {
  projectId?: string;
  project?: string;
}): string => {
  const { projectId, project } = params || {};
  if (isEmpty(projectId)) return '';
  const filter = encodeURIComponent(
    JSON.stringify({
      projectId,
      project: project || '',
      // 用于标识左侧菜单栏当前选中高亮
      menuFrom: MENU_ID_MAP['pipeline_all-projects'],
    }),
  );
  return `${SLASH_G_POP}/Pipeline/ProjectList/Detail?filter=${filter}`;
};

export const goToProjectDetailsPage = (params?: {
  projectId?: string;
  project?: string;
}) => {
  const path = getProjectDetailsPagePath(params);
  if (!path) return;
  history.push(path);
};

/**
 * 页面打点
 * @param pageLogCfgKey 页面打点配置的key
 */
export const reportPageOperationLog = (
  pageLogCfgKey = '',
  cfg?: Record<string, any>,
) => {
  const pageLogCfg = ALL_REPORT_OPERATION_LOG[pageLogCfgKey];
  if (pageLogCfg) {
    reportOperationLog({ ...pageLogCfg, ...cfg } as any, 'page');
  }
};

/**
 * 按钮打点
 * @param btnLogCfgKey 按钮打点配置的key
 */
export const reportBtnOperationLog = (
  btnLogCfgKey = '',
  cfg?: Record<string, any>,
) => {
  const btnLogCfg = ALL_REPORT_OPERATION_LOG[btnLogCfgKey];
  if (btnLogCfg) {
    reportOperationLog({ ...btnLogCfg, ...cfg } as any, 'button');
  }
};

/**
 * 搜索打点
 * @param searchLogCfgKey 搜索打点配置的key
 */
export const reportSearchOperationLog = (
  searchLogCfgKey = '',
  cfg: { query: string; clickId: string; [key: string]: any; },
) => {
  const searchLogCfg = ALL_REPORT_OPERATION_LOG[searchLogCfgKey];
  if (searchLogCfg) {
    reportOperationLog({ ...searchLogCfg, ...cfg }, 'search');
  }
};

/**
 * 转为moment后的日期，无法转为moment时或者转换后的moment是非法时，返回null
 * @param date 日期
 * @returns 转为moment后的日期，无法转为moment时或者转换后的moment是非法时，返回null
 */
export const getNullableMoment = (date?: string): INullableMoment => {
  if (!date) return null;
  const dateM = moment(date);
  return dateM?.isValid() ? dateM : null;
};

export const addUuid = <T,>(data: T): T & IMockIdable => ({
  ...data,
  mock_id: uuid(),
});

export const addUuidBatch = <T,>(list?: T[] | null): (T & IMockIdable)[] => {
  if (!Array.isArray(list) || !list.length) return [];
  return list.map((item) => addUuid<T>(item));
};

export const addMockId = <T,>(data: T): T & IMockIdable => ({
  ...data,
  mock_id: generateMockId(),
});

export const addMockIdBatch = <T,>(list?: T[] | null): (T & IMockIdable)[] => {
  if (!Array.isArray(list) || !list.length) return [];
  return list.map(addMockId);
};

export const removeMockIdBatch = <T,>(
  list?: T[] | null,
): Omit<T, 'mock_id'>[] => {
  if (!Array.isArray(list) || !list.length) return [];
  return list.map((item) => {
    const { mock_id: _mockId, ...rest } = (item || {}) as T & IMockIdable;
    return rest;
  });
};

export const getRecordKeyOfMockable = (record: IMockIdable): string =>
  record?.mock_id || '';

export const generateUUID = (id?: string): string => (!!id ? id : uuid());

export const generateMockId = (id?: string): string =>
  !!id ? id : `mock_${uuid()}`;

export const isMockId = (id?: string) => id?.startsWith('mock_') || false;

export const transform2MockId = (id?: string): string =>
  !id ? '' : `mock_${id}`;

export const formatBoolFalg = (
  payload?: BoolFlag | (string & {}),
  placeholder = '-',
): 'Y' | 'N' | (string & {}) => {
  if (payload === BoolFlag.True) return CONSTANT_TEXTS.YES;
  if (payload === BoolFlag.False) return CONSTANT_TEXTS.NO;
  return payload || placeholder;
};

/**
 * 删除html2canvas生成的垃圾dom节点，不然会导致下载的PDF乱七八糟
 * 垃圾dom节点的特征
 * 1、仅包含style属性
 * 2、style属性有以下三个值
 *  1、display: block;
 *  2、transition-property: none;
 *  3、height：xxxpx; 这个高度不固定的
 * @param element PDF下载时，html2canvas插件回传的dom节点（包含了要下载为PDF的节点）
 */
const removeRublishNodesCreateByHTML2Canvas = (element?: HTMLElement) => {
  const divWithStylePropertyNodes = element?.querySelectorAll(
    'div[style*="display: block"]',
  );
  divWithStylePropertyNodes?.forEach((node) => {
    const nodeAttrNames = node?.getAttributeNames() || [];
    const isOnlyStylePropertyNode =
      nodeAttrNames?.length === 1 && nodeAttrNames[0] === 'style';
    // @ts-ignore
    const nodeStyle = node?.style || {};
    if (
      isOnlyStylePropertyNode &&
      Reflect.has(nodeStyle, 'height') &&
      nodeStyle.transitionProperty === 'none'
    ) {
      node.remove();
    }
  });
};

const getJSPDFInstanceByNode = (cfg: {
  node: HTMLElement;
  html2canvasOptions?: Record<string, any>;
  jsPDFOptions?: Record<string, any>;
}) => {
  const { node, jsPDFOptions, html2canvasOptions } = cfg || {};
  const width = Number.parseFloat(
    getComputedStyle(node).getPropertyValue('width'),
  );
  const height = Number.parseFloat(
    getComputedStyle(node).getPropertyValue('height'),
  );

  return html2pdf()
    .set({
      // PDF自动分页配置
      // pagebreak: { mode: ['avoid-all', 'css'] },
      // [top,left,bottom,right]
      // margin: [0, 10, 0, 10],
      pagebreak: { mode: 'avoid-all' },
      margin: 0,
      image: {
        type: 'jpeg',
        quality: 1,
      },
      enableLinks: true,
      html2canvas: {
        ...html2canvasOptions,
        scale: 3,
        imageTimeout: 0,
        allowTaint: true,
        useCORS: true,
        width,
        height,
        // windowWidth: width,
        // windowHeight: height,
        x: 0,
        y: 0,
        scrollX: 0,
        scrollY: 0,
        onclone: (document: Document, element: HTMLElement) => {
          if (!document || !element) return;
          const rootNode = document.querySelector<HTMLElement>('#root');
          const hideEls = element.querySelectorAll<HTMLElement>(
            `.${captureRemoveClassName}`,
          );
          const showEls = element.querySelectorAll<HTMLElement>(
            `.${captureExpendClassName}`,
          );
          document.body.style.height = 'auto';
          if (rootNode) {
            rootNode.style.height = 'auto';
          }
          hideEls.forEach((el) => {
            el.style.display = 'none';
          });
          showEls.forEach((el) => {
            if (!el) return;
            el.style.display = 'block';
            // @ts-ignore
            el.style['-webkit-box-orient'] = 'unset';
          });
          removeRublishNodesCreateByHTML2Canvas(element);
          html2canvasOptions?.onclone?.(document, element);
        },
      },
      jsPDF: {
        ...jsPDFOptions,
        orientation: width > height ? 'l' : 'p',
        unit: 'px',
        hotfixes: ['px_scaling'],
        format: [width, height],
      },
      thenExternal: () => {},
    })
    .from(node, 'element');
};

export const getPDFFileByNode = async (cfg: {
  node: HTMLElement;
  fileName?: string;
  html2canvasOptions?: Record<string, any>;
  jsPDFOptions?: Record<string, any>;
}): Promise<File> => {
  const { fileName = '', ...restCfg } = cfg || {};
  const fileFullName = `${fileName}.pdf`;
  const pdfBlob = await getJSPDFInstanceByNode(restCfg).output('blob', {
    filename: fileFullName,
  });
  const pdfFile = new File([pdfBlob], fileFullName, {
    type: 'application/pdf',
  });
  return Promise.resolve(pdfFile);
};

export const downloadPDFByNode = async (cfg: {
  node: HTMLElement;
  fileName?: string;
  html2canvasOptions?: Record<string, any>;
  jsPDFOptions?: Record<string, any>;
}) => {
  const { fileName = '', ...restCfg } = cfg || {};

  return getJSPDFInstanceByNode(restCfg).toPdf().save(`${fileName}.pdf`);
};

export const addDays = (date: string, num: number): string => {
  const dateM: moment.Moment | null = date ? moment(date) : null;
  if (!dateM?.isValid()) return '';
  if (typeof num !== 'number' || Number.isNaN(num)) return date;
  return dateM.add(num, 'days').format('YYYY-MM-DD');
};

/**
 * 将指定字符串转换为大写
 * @param char 字符传
 * @returns 将指定字符串转换为大写后的字符串
 */
const toUpperCaseFormatter = (char?: string) => char?.toUpperCase() || '';

/**
 * 获取指定字符串，split后，并且非空的字符数组
 * @param str 字符串
 * @param charFormatter 字符formatter方法
 * @returns 非空的字符数组
 */
const getAvailableChars = (
  str?: string,
  charFormatter?: (char: string) => string,
): string[] => {
  if (!str) return [];
  const chars = str?.split('');
  const result: string[] = [];

  for (const char of chars) {
    if (!char?.trim()) continue;
    const charFormatted = charFormatter?.(char) ?? char;
    result.push(charFormatted);
  }

  return result;
};

/**
 * 字符串排序comparator方法
 * 注意：这是比较两个字符串 对应位置的字符 进行排序，如果前面每一个字符都相同，就比较下一个字符
 * 如果两个长度不一样的字符串，并且短的那个字符串 跟 长的字符串前面的排序都一样，那么短的那个字符串就会排前面，长的排后面
 * 排序规则:
 * 1、优先按照1~9的顺序排列
 * 2、其次按照a-z的顺序排列（忽略大小写）
 * 3、汉字排最后
 * @param a 字符串a
 * @param b 字符串b
 * @returns 排序comparator方法的返回值，正数（a要排在b后面）、负数（a要排在b前面）、0（不交换位置）
 */
export const charsComparator = (a?: string, b?: string): number => {
  if (!a && !b) return 0;
  if (!a) return 1;
  if (!b) return -1;
  if (a === b) return 0;

  const aChars = getAvailableChars(a, toUpperCaseFormatter);
  const bChars = getAvailableChars(b, toUpperCaseFormatter);
  const aCharsLen = aChars.length;
  const bCharsLen = bChars.length;
  const maxCharsLen = Math.max(aCharsLen, bCharsLen);
  let num = 0;

  for (let i = 0;i < maxCharsLen;i++) {
    const aChar = aChars[i];
    const bChar = bChars[i];
    if (aChar === undefined) return -1;
    if (bChar === undefined) return 1;
    if (!/[0-9a-zA-Z]/.test(aChar)) return 1;
    if (!/[0-9a-zA-Z]/.test(bChar)) return -1;
    num = aChar.localeCompare(bChar);
    if (num === 0) continue;
    return num;
  }

  return 0;
};

/**
 * 把对象数组中的Others选项 转移到 数组最后
 * @param charsKey 名称的字段名
 * @param list 对象数组
 * @returns 对象数组
 */
const removeOthers2LastPure = (list?: string[]) => {
  if (!Array.isArray(list) || !list.length) return;
  const othersItemIndex = list.findIndex((item) => {
    return item === OHERSKEY;
  });
  if (othersItemIndex !== -1) {
    const [othersItem] = list.splice(othersItemIndex, 1);
    list.push(othersItem);
  }
};

/**
 * 把对象数组中的Others选项 转移到 数组最后
 * @param charsKey 名称的字段名
 * @param list 对象数组
 * @returns 对象数组
 */
const removeOthers2Last = <T,>(charsKey: keyof T, list?: T[]) => {
  if (!Array.isArray(list) || !list.length) return;
  const othersItemIndex = list.findIndex((item) => {
    const itemText = item?.[charsKey] as string;
    return itemText === OHERSKEY;
  });
  if (othersItemIndex !== -1) {
    const [othersItem] = list.splice(othersItemIndex, 1);
    list.push(othersItem);
  }
};

/**
 * 把对象数组中的Others选项 转移到 数组最后
 * @param getCharts 获取排序字符串的方法
 * @param list 对象数组
 * @returns 对象数组
 */
const removeOthers2LastWithCharsGetter = <T,>(
  getCharts: (payload?: T) => string,
  list?: T[],
) => {
  if (!Array.isArray(list) || !list.length) return;
  const othersItemIndex = list.findIndex((item) => {
    const itemText = getCharts(item);
    return itemText === OHERSKEY;
  });
  if (othersItemIndex !== -1) {
    const [othersItem] = list.splice(othersItemIndex, 1);
    list.push(othersItem);
  }
};

/**
 * 对字符串数组进行排序，按照0~1、a-z的顺序（忽略大小写）
 * @param list 字符串数组
 * @returns 排序后的字符串数组
 */
export const sortByCharsPure = (list?: string[] | null): string[] => {
  if (!Array.isArray(list) || !list.length) return [];
  const listTmp = cloneDeep(list);
  listTmp.sort(charsComparator);
  removeOthers2LastPure(listTmp);
  return listTmp;
};

/**
 * 将指定对象数组按照 自定字段的字符串 进行排序
 * @param charsKey 要排序的字符串的字段名
 * @param list 对象数组
 * @returns 排序后的对象数组
 */
export const sortByChars = <T,>(charsKey: keyof T, list?: T[]): T[] => {
  if (!Array.isArray(list) || !list.length) return [];
  const listTmp = cloneDeep(list);
  listTmp.sort((a, b) => {
    const aChars = a[charsKey];
    const bChars = b[charsKey];
    return charsComparator(aChars as string, bChars as string);
  });
  removeOthers2Last(charsKey, listTmp);
  return listTmp;
};

/**
 * 将指定对象数组按照 getCharts方法返回的字符串 进行排序
 * @param getCharts 获取排序字符串的方法
 * @param list 对象数组
 * @returns 排序后的对象数组
 */
export const sortByCharsWithCharsGetter = <T,>(
  getCharts: (payload?: T) => string,
  list?: T[],
): T[] => {
  if (!Array.isArray(list) || !list.length) return [];
  const listTmp = cloneDeep(list);
  listTmp.sort((a, b) => {
    const aChars = getCharts(a);
    const bChars = getCharts(b);
    return charsComparator(aChars as string, bChars as string);
  });
  removeOthers2LastWithCharsGetter(getCharts, listTmp);
  return listTmp;
};

/**
 * 处理tea组件sortable插件排序时，onChange返回的数据
 * 删除掉tea组件sortable插件排序时，onChange返回的数据中 具有唯一排序限制的排序字段
 * @param uniqueSortKeys 具有唯一激活限制的排序字段集合（即该集合的排序字段，同时仅允许一个字段排序）
 * @param payload tea组件sortable插件排序时，onChange返回的数据
 * @returns 处理后的数据排序数据
 */
export function getSortByWhenHasUniqueSortKeys<T = any>(
  uniqueSortKeys?: string[],
  payload?: SortBy<T>[],
): SortBy<T>[] {
  if (!Array.isArray(payload)) return [];
  if (!Array.isArray(uniqueSortKeys)) return payload;
  const payloadNew = cloneDeep(payload);
  // tea组件返回的最新排序字段key集合
  const payloadSortKeys = payloadNew.map((item) => item?.by);
  // uniqueSortKeys字段索引集合（索引是指在tea组件返回的最新排序字段集合中的索引）（uniqueSortKeys集合中的字段，并且在tea组件返回的最新排序字段集合中存在的）
  const uniqueSortKeysMatched = [];

  /**
   * 遍历uniqueSortKeys集合
   * 如果uniqueSortKey在payloadSortKeys集合中存在，则将uniqueSortKey的索引添加到uniqueSortKeysMatched集合中
   */
  for (const uniqueSortKey of uniqueSortKeys) {
    const uniqueSortKeyMatchedIndex = payloadSortKeys.indexOf(uniqueSortKey);
    if (uniqueSortKeyMatchedIndex !== -1) {
      uniqueSortKeysMatched.push(uniqueSortKeyMatchedIndex);
    }
  }

  /**
   * uniqueSortKeysMatched集合长度大于1，说明存在有唯一排序限制的多个字段同时排序
   * 需要删除掉存在唯一排序限制的多个字段中的多余排序字段（只保留第一个有唯一排序限制的字段）
   */
  if (uniqueSortKeysMatched.length > 1) {
    // 对uniqueSortKeysMatched集合排序，保证索引从小到大
    uniqueSortKeysMatched.sort();
    // 需要删除的排序字段的key的集合
    const needDeleteSortKeys = uniqueSortKeysMatched.slice(1);

    /**
     * 倒序遍历payloadNew集合，进行删除多余排序字段
     */
    for (let i = payloadNew.length - 1;i >= 0;i--) {
      if (needDeleteSortKeys.includes(i)) {
        payloadNew.splice(i, 1);
      }
    }
  }

  // 返回处理后的最新的排序字段集合
  return payloadNew;
}

// 将普通对象转换为FormData
export const object2FormData = (params?: IRecord<any> | null): FormData => {
  const fd = new FormData();
  if (!params) return fd;

  Object.entries(params).forEach((entry) => {
    const [key, val] = entry;
    fd.append(key, val);
  });

  return fd;
};

/**
 * 获取tea的Select类型组件的options（重命名后）
 * @param options tea的Select类型组件的options
 * @param getName 重名名text的方法
 * @returns tea的Select类型组件的options（重命名后）
 */
export const renameOfOptions = (
  options?: IGPSelectOptionWithGroup[] | null,
  getName?: (payload?: string) => string,
): IGPSelectOptionWithGroup[] => {
  if (!Array.isArray(options) || !options.length) return [];
  // @ts-ignore
  return options.map((option) => {
    const { text, ...restOption } = option || {};
    const textTmp = getName?.(text as string) ?? text;
    return { ...restOption, text: textTmp, label: textTmp };
  });
};

/**
 * 表格的列宽（px）
 * @param width 表格的列宽度（在设计稿表格宽度为1094px下的列宽）
 * @returns 表格的列宽百分比
 */
export const getColumnPercentageWidth = (width: number): string =>
  `${(width / 1094) * 100}%`;

/* 获取腾讯员工头像图片链接
 * @param name 名称
 * @param size 尺寸
 * @returns 头像图片链接
 */
export const getTencentAvatarSrc = (name?: string, size = 48): string => {
  if (!name?.trim()) return '';
  if (!size) return '';
  return `https://r.hrc.woa.com/photo/${size}/${name}.png`;
};

export const initOptions = (
  list?: string[] | null,
): IGPSelectOptionWithGroup[] => {
  if (!Array.isArray(list) || !list.length) return [];

  const listTmp = list.map((item) => {
    let text = item === 'OTHER' ? OHERSKEY : item;
    // business owner数据可能是json字符串对象
    try {
      text = (Object.values(JSON.parse(text))[0] as string) || text;
    } catch (err) {}
    return { value: item, text, groupKey: GROUP_KEY, label: text };
  });

  const othersIndex = listTmp.findIndex((item) => item?.text === OHERSKEY);
  // 如果有Others，把Others放到最后
  if (othersIndex !== -1) {
    listTmp.push(listTmp.splice(othersIndex, 1)[0]);
  }

  return listTmp;
};

export const getMatchedValue = (params: {
  values?: string[];
  defaultValue?: string;
  value?: string;
}): string => {
  const { values, defaultValue, value } = params || {};
  const defaultValueTmp = defaultValue || '';
  if (!Array.isArray(values) || !values.length) return defaultValueTmp;
  return value && values.includes(value) ? value : defaultValueTmp;
};

export const resetPageScrollTop = (node?: Element | null) => {
  node?.scrollTo(0, 0);
};

export const addId = <T extends { id: string; }>(data: T): T => {
  const { id } = data || {};
  return { ...data, id: generateUUID(id) };
};

export const addIdBatch = <T extends { id: string; }>(
  list?: T[] | null,
): T[] => {
  if (!Array.isArray(list) || !list.length) return [];
  return list.map(addId);
};

/**
 * 替换指定字符串中符合url格式的子字符串为<a>标签超链接
 * @param text 指定字符串
 * @return string 替换后的结果
 */
export const replaceUrlsWithLinks = (text?: string): string => {
  if (!text) return '';
  // const reg = /((https?):\/\/)?[\w-]+\.([\w-/]+\.?)+(\?([\w-&=%]+)?)?(#[\w-#]+)?/gi;
  // const reg = /(?:(?:https?):\/\/)?[\w.-]+\.[a-zA-Z]{2,}(?:\/[^\s?#]*(?:\?[^\s#]*)?(?:#[^\s]*)?)?/gi;
  const reg =
    /(([a-z]+):\/\/)?[\w.-]+\.[a-zA-Z]{2,}(?:\/[^\s?#]*(?:\?[^\s#]*)?(?:#[^\s]*)?)?/gi;
  return text.replace(reg, (url) => {
    const isStartsWithProtocol =
      url?.startsWith('https') || url?.startsWith('http');
    const urlTmp = isStartsWithProtocol ? url : `//${url}`;
    return `<a href="${urlTmp}" target="_blank" rel="noopener" data-mce-selected="inline-boundary">${url}</a>`;
  });
};

// 特殊HTML字符反转译映射
const UNESCAPE_SPECIAL_HTML_CHAR_MAPPINGS: IRecord<string> = {
  lt: '<',
  gt: '>',
  nbsp: ' ',
  amp: '&',
  quot: '"',
};

/**
 * 反转译HTML字符串中的特殊HTML字符
 * @param str HTML字符串
 * @return 反转译后的HTML字符串
 */
export const unescapeSpecialHTMLChars = (str?: string): string => {
  if (!str) return '';
  return str.replace(/&(lt|gt|nbsp|amp|quot);/gi, (all, code) => {
    return UNESCAPE_SPECIAL_HTML_CHAR_MAPPINGS[code];
  });
};

// 需要转义的正则表达式字符集合
const ESCAPE_SPECIAL_REGEXP_CHARS: string[] = '()[]{}.+?*^$='.split('');

/**
 * 转义正则表达式中的特殊字符，避免在根据用户输入字符串创建正则表达式时潜在报错
 * @param str 正则表达式字符串
 * @return 转义后的正则表达式字符串
 */
export const escapeSpecialRegExpChars = (str?: string): string => {
  if (!str) return '';
  let strTmp = str;
  ESCAPE_SPECIAL_REGEXP_CHARS.forEach((char) => {
    strTmp = strTmp.replaceAll(char, `\\${char}`);
  });
  return strTmp;
};

export { getReverseObj };

export const getPlatformIconList = (platform: any) => {
  const iconList: any = [];
  const platformLowerCase =
    (Array.isArray(platform)
      ? platform.join(',')?.toLowerCase()
      : platform?.trim()?.toLowerCase()) || '';
  if (platformLowerCase.includes('mobile')) {
    iconList.push('mobile');
  }
  if (platformLowerCase.includes('pc')) {
    iconList.push('pc');
  }
  if (isIncludeConsole(platformLowerCase)) {
    iconList.push('console');
  }
  return iconList;
};
